import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  CheckCircle,
  XCircle,
  RotateCcw,
  Search,
  Save,
  AlertTriangle,
  Trash2,
  Plus,
  LayoutGrid,
  List,
  ChevronDown,
  Wrench,
  Sparkles,
  Clipboard,
} from "lucide-react";
import RichInvoiceGrid from "./RichInvoiceGrid";
import InvoiceLineEvidence from "./InvoiceLineEvidence";
import { GEMINI_FLASH_MODEL } from "../utils/geminiModel";
import {
  DOC_TYPES,
  TYPE_CODES,
  groupPagesByDocType as groupPagesBySchema,
  GROUP_META,
  DOC_GROUP_ORDER,
  groupOf,
  labelOf,
  prefixOf,
} from "../config/docTypeSchema";

// ════════════════════════════════════════════════════════════════════
// 학습데이타검증 — 3-panel UI
//   Left   : 샘플 리스트 + 필터
//   Middle : S3 이미지 뷰어 (presigned URL)
//   Right  : verified_data 편집 폼 (header + items)
// ════════════════════════════════════════════════════════════════════

const HEADER_FIELDS = [
  "Blno",
  "Seller_name",
  "Buyer_name",
  "Invoice_no",
  "Grossweight",
  "Package",
];
const ITEM_FIELDS = [
  "Partno",
  "Description",
  "Composition",
  "HSCode",
  "Unit Quantity",
  "UnitType",
  "Price",
  "Amount",
  "Netweight",
  "COO",
  "Incoterms",
];

// Google AI Studio 스타일 — 색은 최소, 왼쪽 작은 도트로 상태만 표현
const QUALITY_DOTS = {
  gold: "bg-amber-500",
  silver: "bg-slate-400",
  needs_human: "bg-red-500",
  human_verified: "bg-emerald-500",
  rejected: "bg-stone-500",
  error: "bg-rose-600",
};

function qualityBadge(label) {
  const dot = QUALITY_DOTS[label] || "bg-slate-400";
  return (
    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border border-slate-200 bg-white text-slate-700">
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      {label}
    </span>
  );
}

// ── 페이지 그룹핑 어댑터 ─────────────────────────────────────────────
// 스키마의 groupPagesByDocType 는 type 단위 flat 배열을 반환.
// 뷰어는 6-group × 섹션(source/result) 로 2차 그룹핑해야 해서 한 번 더 감싼다.
// 섹션 구분은 GROUP_META[groupKey].section 을 따름.
function groupPagesForViewer(pages) {
  const flat = groupPagesBySchema(pages); // [{group:{key,groupKey,...}, items}]
  const sections = {}; // { source: {primary:[], supporting:[]...}, result: {...} }
  for (const entry of flat) {
    const gk = entry.group.groupKey;
    const section = (GROUP_META[gk] && GROUP_META[gk].section) || "source";
    if (!sections[section]) sections[section] = {};
    if (!sections[section][gk]) sections[section][gk] = [];
    sections[section][gk].push(entry);
  }
  // 각 섹션 안에서는 DOC_GROUP_ORDER 를 따름
  const build = (sec) => {
    if (!sections[sec]) return [];
    return DOC_GROUP_ORDER.filter((gk) => sections[sec][gk]).map((gk) => ({
      groupKey: gk,
      groupLabel: GROUP_META[gk].label,
      entries: sections[sec][gk],
    }));
  };
  return { source: build("source"), result: build("result") };
}

const TrainingVerifyPanel = () => {
  // ─── 상태 ────────────────────────────────────────────────────────
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [loading, setLoading] = useState(false);

  const [filterSource, setFilterSource] = useState("critic_distillation");
  const [filterQuality, setFilterQuality] = useState("");
  const [filterVerified, setFilterVerified] = useState("");
  const [filterDocType, setFilterDocType] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [sortBy, setSortBy] = useState("quality_label");
  const [filterCollapsed, setFilterCollapsed] = useState(false);
  const [activePreset, setActivePreset] = useState("");

  // customs_declaration_gt 전용 — 신고일 구간 / B/L / 수입자
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [filterBl, setFilterBl] = useState("");
  const [filterImporter, setFilterImporter] = useState("");

  // 왼쪽 패널 모드: 'sample' (건별 리스트) | 'docType' (타입별 집계)
  const [leftPanelMode, setLeftPanelMode] = useState(
    () => localStorage.getItem("training_verify_left_panel_mode") || "sample",
  );
  useEffect(() => {
    localStorage.setItem("training_verify_left_panel_mode", leftPanelMode);
  }, [leftPanelMode]);
  // 타입별 뷰 상태
  const [selectedDocType, setSelectedDocType] = useState(null);
  const [typePages, setTypePages] = useState([]); // [{id, source_sample_id, file_name, page, doc_type, confidence, url}]
  const [typeLoading, setTypeLoading] = useState(false);
  const [typeTotal, setTypeTotal] = useState(0);
  const [typeCounts, setTypeCounts] = useState({}); // { CI: 123, BL: 45, ... } — 좌측 트리 배지
  const [confMinFilter, setConfMinFilter] = useState(0); // 타입별 뷰 최소신뢰도 필터
  const [confSuspectOnly, setConfSuspectOnly] = useState(false); // 신뢰도 < 1.0 만 — 오분류 의심건 (이메일 등)
  const [typePage, setTypePage] = useState(1); // 타입별 뷰 페이지네이션 (1-based)
  const [typePageSize, setTypePageSize] = useState(() =>
    parseInt(
      localStorage.getItem("training_verify_type_page_size") || "60",
      10,
    ),
  );
  const [typeGridCols, setTypeGridCols] = useState(() => {
    const s = parseInt(
      localStorage.getItem("training_verify_type_cols") || "0",
      10,
    );
    return Number.isFinite(s) && s >= 1 && s <= 12 ? s : 0; // 0 = auto-fill
  });
  useEffect(() => {
    localStorage.setItem("training_verify_type_cols", String(typeGridCols));
  }, [typeGridCols]);

  // 불일치 그룹 뷰: { type, total_mismatch, groups: [{llm_type, n, sample_pages}] }
  const [mismatchGroupsData, setMismatchGroupsData] = useState(null);
  const [mismatchGroupsLoading, setMismatchGroupsLoading] = useState(false);
  const [mismatchBulkRunning, setMismatchBulkRunning] = useState(false);
  const [mismatchBulkProgress, setMismatchBulkProgress] = useState({
    done: 0,
    total: 0,
    label: "",
  });
  const [mismatchExpandedGroup, setMismatchExpandedGroup] = useState(null); // 펼친 LLM 제안 타입
  // 불일치 그룹 뷰: 우측 패널 뷰어 미리보기 — { id, page, url, llm_type, llm_confidence, confidence, llm_reason, file_name, group_from }
  const [mismatchPreviewPage, setMismatchPreviewPage] = useState(null);

  // 타입별 뷰 우측: 분류 팔레트 접힘 상태
  const [palettePinned, setPalettePinned] = useState(
    () => localStorage.getItem("training_verify_palette_pinned") !== "0",
  );
  useEffect(() => {
    localStorage.setItem(
      "training_verify_palette_pinned",
      palettePinned ? "1" : "0",
    );
  }, [palettePinned]);

  // 샘플 뷰 좌측: doc_type 설명 팝업
  const [showDocTypeHelp, setShowDocTypeHelp] = useState(false);
  useEffect(() => {
    localStorage.setItem(
      "training_verify_type_page_size",
      String(typePageSize),
    );
  }, [typePageSize]);
  const [typeSelection, setTypeSelection] = useState(() => new Set()); // 썸네일 선택(멀티)
  const [typeHoverPage, setTypeHoverPage] = useState(null); // 썸네일 hover시 프리뷰
  const [typePreviewPage, setTypePreviewPage] = useState(null); // 클릭시 단일 프리뷰
  // LLM 재검증 — {id:page → {llm_type, llm_confidence, llm_reason, agrees, error?}}
  const [llmResults, setLlmResults] = useState(() => new Map());
  const [llmRunning, setLlmRunning] = useState(false);
  const [llmProgress, setLlmProgress] = useState({ done: 0, total: 0 });
  const [llmOnlyDisagree, setLlmOnlyDisagree] = useState(false);
  const [llmModel, setLlmModel] = useState(GEMINI_FLASH_MODEL);
  const [llmPass, setLlmPass] = useState(1);
  // 프롬프트 편집 — pass 별로 localStorage 에 저장
  const promptStorageKey = (p) => `training_verify_custom_prompt_pass${p}`;
  const [customPrompt, setCustomPrompt] = useState(
    () => localStorage.getItem(promptStorageKey(1)) || "",
  );
  const [defaultPrompt, setDefaultPrompt] = useState(""); // 백엔드에서 불러온 원본 (초기화용 & 에디터 placeholder)
  const [promptEditorOpen, setPromptEditorOpen] = useState(false);
  const [promptDraft, setPromptDraft] = useState("");
  // pass 전환 시 해당 pass 의 customPrompt 로 교체
  useEffect(() => {
    setCustomPrompt(localStorage.getItem(promptStorageKey(llmPass)) || "");
    setDefaultPrompt(""); // 다시 불러오도록 초기화
  }, [llmPass]);

  // 리스트 표시 모드: 'card' (기존) | 'grid' (밀집 그리드)
  const [listMode, setListMode] = useState(
    () => localStorage.getItem("training_verify_list_mode") || "card",
  );
  useEffect(() => {
    localStorage.setItem("training_verify_list_mode", listMode);
  }, [listMode]);

  // 좌측 패널 폭 — 사용자 조정 가능 (localStorage)
  const LEFT_MIN = 240;
  const LEFT_MAX = 900;
  const leftDefault =
    leftPanelMode === "docType" ? 300 : listMode === "grid" ? 720 : 340;
  const [leftPanelWidth, setLeftPanelWidth] = useState(() => {
    const saved = parseInt(
      localStorage.getItem("training_verify_left_width") || "",
      10,
    );
    return Number.isFinite(saved)
      ? Math.min(LEFT_MAX, Math.max(LEFT_MIN, saved))
      : 340;
  });
  const [isLeftResizing, setIsLeftResizing] = useState(false);
  // 저장된 값 없고 모드 전환 시엔 각 모드 기본폭으로 자동 맞춤 (사용자가 한번이라도 드래그했으면 그 값 유지)
  const [leftUserAdjusted, setLeftUserAdjusted] = useState(
    () => !!localStorage.getItem("training_verify_left_width"),
  );
  useEffect(() => {
    if (!leftUserAdjusted) setLeftPanelWidth(leftDefault);
  }, [leftDefault, leftUserAdjusted]);
  const leftWidth = leftPanelWidth;

  // 다중 선택 (벌크 액션용)
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  // 다중 페이지 (신고필증 GT 등: 운송서류/CI/PL 등이 한 PDF에 같이 있을 때)
  const [pagesList, setPagesList] = useState([]); // [{page, doc_type, confidence, s3_key, url}]
  const [currentPageIdx, setCurrentPageIdx] = useState(0);
  const [editedData, setEditedData] = useState(null);
  const [saving, setSaving] = useState(false);

  const [stats, setStats] = useState(null);

  // 오른쪽 패널 뷰 모드: 'form' | 'grid'
  const [viewMode, setViewMode] = useState("form");
  const gridRef = useRef(null);
  const gridId = "training_verify_items_grid";

  // ─── 툴바 상태 ───────────────────────────────────────────────────
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [verifyEnabled, setVerifyEnabled] = useState(false);
  const [verificationMode, setVerificationMode] = useState("parallel");
  const [showVerifyDropdown, setShowVerifyDropdown] = useState(false);
  const [showToolsMenu, setShowToolsMenu] = useState(false);
  const [isReExtracting, setIsReExtracting] = useState(false);
  const [draftData, setDraftData] = useState(null); // 재추출 draft
  const [draftApplied, setDraftApplied] = useState(null); // { model, templateName, appliedHeaders: string[], appliedItems: bool }

  // ─── HS 자동분류 상태 ───────────────────────────────────────────
  const [isHsClassifying, setIsHsClassifying] = useState(false);
  const [hsClassifyStats, setHsClassifyStats] = useState(null); // { total, success, requires_review, confidence_distribution }

  // ─── AI 분류 근거 modal — cell 클릭 시 진입 ─────────────────────
  const [evidenceModal, setEvidenceModal] = useState(null); // { rowIndex, description, composition } | null

  // ─── 우측 패널 리사이저 ──────────────────────────────────────────
  const RIGHT_MIN = 420;
  const RIGHT_MAX = 1100;
  const [rightPanelWidth, setRightPanelWidth] = useState(() => {
    const saved = parseInt(
      localStorage.getItem("training_verify_right_width") || "720",
      10,
    );
    return Number.isFinite(saved)
      ? Math.min(RIGHT_MAX, Math.max(RIGHT_MIN, saved))
      : 720;
  });
  const [isResizing, setIsResizing] = useState(false);

  useEffect(() => {
    if (!isResizing) return;
    const onMove = (e) => {
      const w = window.innerWidth - e.clientX;
      setRightPanelWidth(Math.min(RIGHT_MAX, Math.max(RIGHT_MIN, w)));
    };
    const onUp = () => {
      setIsResizing(false);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      // 리사이즈 끝나면 그리드 재계산 + 폭 저장
      setTimeout(() => {
        if (window.AUIGrid) {
          try {
            window.AUIGrid.resize(`#${gridId}`);
          } catch (e) {}
        }
      }, 50);
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [isResizing]);

  // 리사이즈 종료 시 localStorage에 저장
  useEffect(() => {
    if (!isResizing) {
      localStorage.setItem(
        "training_verify_right_width",
        String(rightPanelWidth),
      );
    }
  }, [isResizing, rightPanelWidth]);

  // ─── 좌측 패널 리사이저 ─────────────────────────────────────
  useEffect(() => {
    if (!isLeftResizing) return;
    const onMove = (e) => {
      // 좌측 패널 시작 = 0. 마우스 x 좌표가 곧 폭.
      const w = e.clientX;
      setLeftPanelWidth(Math.min(LEFT_MAX, Math.max(LEFT_MIN, w)));
      if (!leftUserAdjusted) setLeftUserAdjusted(true);
    };
    const onUp = () => {
      setIsLeftResizing(false);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [isLeftResizing, leftUserAdjusted]);
  useEffect(() => {
    if (!isLeftResizing && leftUserAdjusted) {
      localStorage.setItem(
        "training_verify_left_width",
        String(leftPanelWidth),
      );
    }
  }, [isLeftResizing, leftPanelWidth, leftUserAdjusted]);

  const verifyDropdownRef = useRef(null);
  const toolsMenuRef = useRef(null);

  // 템플릿 목록 로드 (로그인 사용자 기준)
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    fetch("/api/templates", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((arr) => {
        if (!Array.isArray(arr)) {
          setTemplates([]);
          return;
        }
        setTemplates(arr);
        // 기본 템플릿: localStorage 설정값 > "표준인보이스" > 첫번째
        const pinned = localStorage.getItem("docuai_default_template");
        const pickByName = (name) => arr.find((t) => t.name === name);
        const picked =
          (pinned && pickByName(pinned)) ||
          pickByName("표준인보이스") ||
          arr[0];
        if (picked) setSelectedTemplateId(picked.id);
      })
      .catch((e) => console.warn("templates fetch fail", e));
  }, []);

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const onClick = (e) => {
      if (
        verifyDropdownRef.current &&
        !verifyDropdownRef.current.contains(e.target)
      )
        setShowVerifyDropdown(false);
      if (toolsMenuRef.current && !toolsMenuRef.current.contains(e.target))
        setShowToolsMenu(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // 단축키 도움말 토글
  const [showShortcuts, setShowShortcuts] = useState(false);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedTemplateId) || null,
    [templates, selectedTemplateId],
  );

  // 재추출 — 백엔드에 S3 이미지 + 템플릿으로 요청 (DB 미저장, draft만 반환)
  const reExtract = useCallback(async () => {
    if (!selectedId || !selectedDetail) {
      alert("먼저 샘플을 선택하세요.");
      return;
    }
    if (!selectedTemplate) {
      alert("템플릿을 선택하세요.");
      return;
    }
    setIsReExtracting(true);
    setDraftData(null);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`/api/training-samples/${selectedId}/reextract`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ templateId: selectedTemplate.id }),
      });
      const json = await res.json();
      if (!res.ok || !json.success)
        throw new Error(json.error || "재추출 실패");

      // 응답에서 header + items 형태로 정규화
      const raw = json.draft || {};
      const draft = {};
      HEADER_FIELDS.forEach((f) => {
        draft[f] = raw[f] ?? "";
      });
      const items = Array.isArray(raw.items) ? raw.items : [];
      draft.items = items.map((it) => {
        const row = {};
        ITEM_FIELDS.forEach((f) => {
          row[f] = it[f] ?? "";
        });
        return row;
      });
      setDraftData({
        data: draft,
        model: json.model,
        templateName: json.templateName,
        elapsedMs: json.elapsedMs,
      });
    } catch (e) {
      console.error("reextract error:", e);
      alert(`재추출 실패: ${e.message}`);
    } finally {
      setIsReExtracting(false);
    }
  }, [selectedId, selectedDetail, selectedTemplate]);

  // ─── 퀵필터 프리셋 ────────────────────────────────────────────────
  const QUICK_PRESETS = useMemo(
    () => [
      {
        key: "",
        label: "전체",
        apply: () => ({ quality: "", verified: "", sort: "quality_label" }),
      },
      {
        key: "problem",
        label: "문제건",
        desc: "needs_human / 저신뢰도",
        apply: () => ({
          quality: "needs_human",
          verified: "",
          sort: "confidence_asc",
        }),
      },
      {
        key: "unverified",
        label: "미검증",
        apply: () => ({
          quality: "",
          verified: "false",
          sort: "confidence_asc",
        }),
      },
      {
        key: "low_conf",
        label: "신뢰도낮음",
        apply: () => ({ quality: "", verified: "", sort: "confidence_asc" }),
      },
      {
        key: "rejected",
        label: "보류됨",
        apply: () => ({
          quality: "rejected",
          verified: "",
          sort: "created_at_desc",
        }),
      },
      {
        key: "verified",
        label: "검증완료",
        apply: () => ({
          quality: "human_verified",
          verified: "true",
          sort: "created_at_desc",
        }),
      },
    ],
    [],
  );

  const applyPreset = useCallback(
    (key) => {
      const p = QUICK_PRESETS.find((x) => x.key === key);
      if (!p) return;
      const out = p.apply();
      setFilterQuality(out.quality);
      setFilterVerified(out.verified);
      setSortBy(out.sort);
      setActivePreset(key);
      setSelectedIds(new Set());
    },
    [QUICK_PRESETS],
  );

  // ─── HS 자동분류 (Evidence 기반) ──────────────────────────────────
  // 1) 빈 HSCode cell 만 분류 — 사용자 수동 입력 보존
  // 2) /batch-with-evidence 5-layer 호출 (precedent + master + Gemini + embedding + reverse)
  // 3) HIGH confidence 만 HSCode 자동 채움 + _provenance='ai_suggested'
  //    MEDIUM/LOW 는 _aiSuggestion 메타만 저장 (cell tint + modal 확정 후 채움)
  const autoHsClassify = useCallback(async () => {
    const rows = editedData?.items || [];
    const validRows = rows
      .map((r, i) => ({
        idx: i,
        description: (r.Description || "").trim(),
        composition: (r.Composition || "").trim(),
        existingHs: (r.HSCode || "").toString().trim(),
      }))
      .filter((r) => r.description && !r.existingHs);

    if (validRows.length === 0) {
      alert(
        "빈 HSCode 행이 없습니다. (이미 채워진 cell 은 자동분류에서 보존됩니다.)",
      );
      return;
    }

    setIsHsClassifying(true);
    setHsClassifyStats(null);
    try {
      const items = validRows.map((r, i) => ({
        index: i,
        description: r.description,
        composition: r.composition,
      }));
      const res = await fetch("/api/hs-classify/batch-with-evidence", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      const updated = [...rows];
      (data.results || []).forEach((r) => {
        const origIdx = validRows[r.index]?.idx;
        if (origIdx == null || !r.ok) return;
        const suggestion = {
          hsk: r.hsk_10digit,
          hs6: r.hs_6digit,
          hs4: r.hs_4digit,
          chapter: r.chapter,
          confidence: r.confidence,
          score: r.score,
          popum_ko: r.popum_ko,
          popum_en: r.popum_en,
          evidence_summary: r.evidence_summary,
          keyword: r.keyword,
        };
        const nextRow = { ...updated[origIdx], _aiSuggestion: suggestion };
        // HIGH 만 cell 자동 채움 — 사용자가 modal 에서 확정하면 'user_confirmed' 로 승격
        if (r.confidence === "high" && r.hsk_10digit) {
          nextRow.HSCode = r.hsk_10digit;
          nextRow._provenance = "ai_suggested";
        }
        updated[origIdx] = nextRow;
      });
      setEditedData((prev) => ({ ...prev, items: updated }));
      setHsClassifyStats({
        total: data.total,
        success: data.success,
        confidence_distribution: data.confidence_distribution,
      });
    } catch (e) {
      alert(`HS 자동분류 실패: ${e.message}`);
    } finally {
      setIsHsClassifying(false);
    }
  }, [editedData]);

  // ─── 선택 관리 ────────────────────────────────────────────────────
  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  // ─── 목록 조회 ────────────────────────────────────────────────────
  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        source: filterSource,
        pageSize: pageSize.toString(),
        page: page.toString(),
        sort: sortBy,
      });
      if (filterQuality) params.append("quality_label", filterQuality);
      if (filterVerified) params.append("verified", filterVerified);
      if (filterDocType) params.append("document_type", filterDocType);
      if (searchQ) params.append("q", searchQ);
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (filterBl) params.append("bl", filterBl);
      if (filterImporter) params.append("importer", filterImporter);

      const r = await fetch(`/api/training-samples?${params}`);
      const data = await r.json();
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error("목록 조회 실패", e);
    } finally {
      setLoading(false);
    }
  }, [
    filterSource,
    filterQuality,
    filterVerified,
    filterDocType,
    searchQ,
    sortBy,
    page,
    pageSize,
    dateFrom,
    dateTo,
    filterBl,
    filterImporter,
  ]);

  const fetchStats = useCallback(async () => {
    try {
      const r = await fetch("/api/training-samples/stats");
      setStats(await r.json());
    } catch (e) {
      console.error("stats fail", e);
    }
  }, []);

  // ─── 타입별 페이지 조회 ────────────────────────────────────────────
  const fetchTypePages = useCallback(
    async (
      type,
      {
        conf_min = 0,
        conf_max = null,
        llm_mismatch_only = false,
        limit = 60,
        offset = 0,
      } = {},
    ) => {
      if (!type) return;
      setTypeLoading(true);
      try {
        const params = new URLSearchParams({
          type,
          limit: String(limit),
          offset: String(offset),
        });
        if (filterSource) params.append("source", filterSource);
        if (conf_min > 0) params.append("conf_min", String(conf_min));
        if (conf_max != null) params.append("conf_max", String(conf_max));
        if (llm_mismatch_only) params.append("llm_mismatch_only", "1");
        const r = await fetch(
          `/api/training-samples/pages/by-doctype?${params}`,
        );
        const data = await r.json();
        const pageArr = Array.isArray(data.pages) ? data.pages : [];
        setTypePages(pageArr);
        setTypeTotal(data.total || 0);
        // 캐시된 LLM 결과 (extraction_meta.pages[].llm_verify) hydration
        setLlmResults((prev) => {
          const n = new Map(prev);
          for (const p of pageArr) {
            if (p.llm_verify && typeof p.llm_verify === "object") {
              const k = `${p.id}:${p.page}`;
              if (!n.has(k)) {
                n.set(k, {
                  id: p.id,
                  page: p.page,
                  llm_type: p.llm_verify.type,
                  llm_confidence: p.llm_verify.confidence,
                  llm_reason: p.llm_verify.reason,
                  agrees: p.llm_verify.type === (p.doc_type || type),
                  cached_at: p.llm_verify.at,
                });
              }
            }
          }
          return n;
        });
      } catch (e) {
        console.error("type-pages fail", e);
        setTypePages([]);
        setTypeTotal(0);
      } finally {
        setTypeLoading(false);
      }
    },
    [filterSource],
  );

  // ─── 불일치 그룹 조회 ──────────────────────────────────────
  const fetchMismatchGroups = useCallback(
    async (type) => {
      if (!type) {
        setMismatchGroupsData(null);
        return;
      }
      setMismatchGroupsLoading(true);
      try {
        const params = new URLSearchParams({ type, preview: "8" });
        if (filterSource) params.append("source", filterSource);
        const r = await fetch(
          `/api/training-samples/pages/by-doctype/llm-groups?${params}`,
        );
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        setMismatchGroupsData(data);
      } catch (e) {
        console.error("mismatch-groups fail", e);
        setMismatchGroupsData(null);
      } finally {
        setMismatchGroupsLoading(false);
      }
    },
    [filterSource],
  );

  // ─── 불일치 그룹 일괄 적용 (한 그룹 전체를 새 타입으로) ─────────
  // scope: 'preview' (현재 화면에 보이는 미리보기 N건만) | 'all' (그룹 전체 — DB 에서 재조회)
  const applyMismatchGroupBulk = useCallback(
    async (group, newType, scope = "all") => {
      if (!group || !newType) return;
      if (mismatchBulkRunning) return;
      const currentType = mismatchGroupsData?.type;
      if (!currentType) return;

      let items = [];
      if (scope === "preview") {
        items = (group.sample_pages || []).map((p) => ({
          id: p.id,
          page: p.page,
        }));
      } else {
        // 그룹 전체 — by-doctype 로 mismatch 만 조회 후 llm_verify.type 으로 필터
        const PAGE_SZ = 200;
        let offset = 0;
        while (true) {
          const params = new URLSearchParams({
            type: currentType,
            limit: String(PAGE_SZ),
            offset: String(offset),
            llm_mismatch_only: "1",
          });
          if (filterSource) params.append("source", filterSource);
          const r = await fetch(
            `/api/training-samples/pages/by-doctype?${params}`,
          );
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          const d = await r.json();
          const arr = (d.pages || []).filter(
            (p) => p.llm_verify && p.llm_verify.type === group.llm_type,
          );
          for (const p of arr) items.push({ id: p.id, page: p.page });
          if (!d.pages || d.pages.length < PAGE_SZ) break;
          offset += PAGE_SZ;
        }
      }
      if (items.length === 0) {
        alert("대상 페이지가 없습니다.");
        return;
      }

      const confirmMsg = `${currentType} 그룹의 LLM 제안 [${group.llm_type}] · ${items.length.toLocaleString()}건을 → [${newType}] 으로 일괄 변경합니다.\n계속할까요?`;
      if (!window.confirm(confirmMsg)) return;

      setMismatchBulkRunning(true);
      setMismatchBulkProgress({
        done: 0,
        total: items.length,
        label: `${group.llm_type} → ${newType}`,
      });
      try {
        const CHUNK = 500;
        let doneTotal = 0;
        for (let i = 0; i < items.length; i += CHUNK) {
          const slice = items.slice(i, i + CHUNK);
          const r = await fetch("/api/training-samples/pages/doc-type-bulk", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              items: slice,
              new_doc_type: newType,
              verified_by: "mismatch-bulk",
            }),
          });
          if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
          const data = await r.json();
          doneTotal += data.processed || 0;
          setMismatchBulkProgress({
            done: doneTotal,
            total: items.length,
            label: `${group.llm_type} → ${newType}`,
          });
        }
        alert(`완료: ${doneTotal.toLocaleString()}건 적용됨.`);
        // 데이터 재조회
        await Promise.all([
          fetchMismatchGroups(currentType),
          fetchTypeCounts(),
        ]);
      } catch (e) {
        console.error("bulk apply fail", e);
        alert(`일괄 적용 실패: ${e.message}`);
      } finally {
        setMismatchBulkRunning(false);
      }
    },
    [
      mismatchGroupsData,
      filterSource,
      fetchMismatchGroups,
      mismatchBulkRunning,
    ],
  );

  // 개별 페이지 doc_type 변경 (타입별 뷰 + 샘플 뷰 공용)
  const updatePageDocType = useCallback(async (sampleId, pageNum, newType) => {
    try {
      const r = await fetch(
        `/api/training-samples/${sampleId}/pages/${pageNum}/doc-type`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ doc_type: newType }),
        },
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const body = await r.json().catch(() => ({}));
      const newPrimaryPage = body.primary_page;
      const newPrimaryType = body.primary_doc_type;

      // 타입별 뷰: 현재 목록에서 해당 항목 제거 (현재 타입과 다르면 사라져야 함)
      setTypePages((prev) =>
        prev.filter((p) => !(p.id === sampleId && p.page === pageNum)),
      );
      setTypeTotal((t) => Math.max(0, t - 1));
      // 샘플 뷰: pagesList 에서 해당 페이지 doc_type 업데이트
      setPagesList((prev) =>
        prev.map((p) => (p.page === pageNum ? { ...p, doc_type: newType } : p)),
      );
      // 샘플 상세: 백엔드가 primary 를 재계산했으면 즉시 반영 (탭/썸네일 일관성)
      if (newPrimaryType) {
        setSelectedDetail((prev) => {
          if (!prev || prev.id !== sampleId) return prev;
          const nextMeta = {
            ...(prev.extraction_meta || {}),
            primary_page: newPrimaryPage,
            primary_doc_type: newPrimaryType,
          };
          return {
            ...prev,
            extraction_meta: nextMeta,
            primary_doc_type: newPrimaryType,
          };
        });
      }
      // 카운트 배지 갱신 (백그라운드)
      fetchTypeCounts();
      return true;
    } catch (e) {
      console.error("page doc-type update fail", e);
      alert(`타입 변경 실패: ${e.message}`);
      return false;
    }
  }, []);

  // ─── LLM 재검증: 현재 보이는 typePages 를 Gemini 로 재분류 ─────────
  //   * 타입별 뷰에서만 사용. items(선택된 것) 또는 전체(typePages) 대상.
  //   * 결과는 llmResults Map 에 누적, 이미 있는 키는 덮어씀.
  const runLlmReclassify = useCallback(
    async (items) => {
      if (!items || items.length === 0) return;
      setLlmRunning(true);
      setLlmProgress({ done: 0, total: items.length });
      try {
        // 서버쪽 max 200 이므로 배치로 쪼개서 전송
        const CHUNK = 100;
        const accum = new Map(llmResults);
        let done = 0;
        for (let i = 0; i < items.length; i += CHUNK) {
          const slice = items.slice(i, i + CHUNK);
          const r = await fetch("/api/training-samples/reclassify-batch", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              items: slice.map((p) => ({
                id: p.id,
                page: p.page,
                s3_key: p.s3_key,
                doc_type: p.doc_type || selectedDocType,
              })),
              concurrency: 10,
              model: llmModel,
              pass: llmPass,
              ...(customPrompt && customPrompt.trim().length > 50
                ? { customPrompt }
                : {}),
            }),
          });
          if (!r.ok) {
            const txt = await r.text();
            throw new Error(`HTTP ${r.status}: ${txt}`);
          }
          const data = await r.json();
          for (const res of data.results || []) {
            accum.set(`${res.id}:${res.page}`, res);
          }
          done += slice.length;
          setLlmProgress({ done, total: items.length });
          // 중간 반영 (UX)
          setLlmResults(new Map(accum));
        }
      } catch (e) {
        console.error("LLM reclassify fail", e);
        alert(`LLM 재검증 실패: ${e.message}`);
      } finally {
        setLlmRunning(false);
      }
    },
    [llmResults, llmModel, llmPass, customPrompt, selectedDocType],
  );

  // ─── 프롬프트 편집 관련 핸들러 ─────────────────────────────
  const openPromptEditor = useCallback(async () => {
    setPromptDraft(customPrompt || defaultPrompt || "");
    setPromptEditorOpen(true);
    // 기본 프롬프트가 아직 로드 안됐으면 백엔드에서 가져옴 (placeholder/비교용)
    if (!defaultPrompt) {
      try {
        const r = await fetch(
          `/api/training-samples/classify-prompt?pass=${llmPass}`,
        );
        if (r.ok) {
          const d = await r.json();
          setDefaultPrompt(d.prompt || "");
          if (!customPrompt) setPromptDraft(d.prompt || "");
        }
      } catch (e) {
        console.warn("load default prompt fail", e.message);
      }
    }
  }, [customPrompt, defaultPrompt, llmPass]);

  const savePromptDraft = useCallback(() => {
    const trimmed = (promptDraft || "").trim();
    if (trimmed.length < 50) {
      alert("프롬프트가 너무 짧습니다 (50자 이상 필요).");
      return;
    }
    // 기본 프롬프트와 동일하면 custom 해제
    if (defaultPrompt && trimmed === defaultPrompt.trim()) {
      localStorage.removeItem(promptStorageKey(llmPass));
      setCustomPrompt("");
    } else {
      localStorage.setItem(promptStorageKey(llmPass), promptDraft);
      setCustomPrompt(promptDraft);
    }
    setPromptEditorOpen(false);
  }, [promptDraft, defaultPrompt, llmPass]);

  const resetPromptToDefault = useCallback(() => {
    if (!window.confirm("커스텀 프롬프트를 삭제하고 기본값으로 되돌릴까요?"))
      return;
    localStorage.removeItem(promptStorageKey(llmPass));
    setCustomPrompt("");
    setPromptDraft(defaultPrompt || "");
  }, [llmPass, defaultPrompt]);

  // ─── 전체 타입 LLM 재검증 — by-doctype 을 offset 루프로 전부 수집 후 일괄 재분류
  //   overrides: { type?, suspectOnly? } — ⚡ 원클릭 버튼에서 state 업데이트를 기다리지 않고 직접 지정 가능
  const runLlmReclassifyAllInType = useCallback(
    async (overrides = {}) => {
      const effectiveType = overrides.type || selectedDocType;
      const effectiveSuspect =
        overrides.suspectOnly != null ? overrides.suspectOnly : confSuspectOnly;
      const effectiveModel = overrides.model || llmModel;
      if (!effectiveType) return;
      if (llmRunning) return;
      const PAGE_SZ = 200;
      try {
        setLlmRunning(true);
        setLlmProgress({ done: 0, total: 0 });
        // 1. 전체 페이지 목록 수집
        const all = [];
        let offset = 0;
        let total = 0;
        while (true) {
          const params = new URLSearchParams({
            type: effectiveType,
            limit: String(PAGE_SZ),
            offset: String(offset),
          });
          if (filterSource) params.append("source", filterSource);
          if (confMinFilter > 0)
            params.append("conf_min", String(confMinFilter));
          if (effectiveSuspect) params.append("conf_max", "1.0");
          const r = await fetch(
            `/api/training-samples/pages/by-doctype?${params}`,
          );
          if (!r.ok) throw new Error(`fetch fail HTTP ${r.status}`);
          const d = await r.json();
          if (offset === 0) total = d.total || 0;
          for (const p of d.pages || []) all.push(p);
          if (!d.pages || d.pages.length < PAGE_SZ) break;
          offset += PAGE_SZ;
          if (all.length >= total) break;
        }
        if (all.length === 0) {
          alert("대상 페이지가 없습니다.");
          setLlmRunning(false);
          return;
        }
        // 2. 확인
        const confirmMsg = `${effectiveSuspect ? "⚠ 의심건 " : "전체 "}${all.length.toLocaleString()}개 페이지 (${effectiveType}) 를 LLM 재검증합니다.\n모델: ${effectiveModel} / Pass ${llmPass}${customPrompt ? " / ✏️ 커스텀 프롬프트" : ""}\n예상 소요: ~${Math.ceil((all.length / 10) * 2)}초 (병렬 10)\n진행할까요?`;
        if (!window.confirm(confirmMsg)) {
          setLlmRunning(false);
          return;
        }
        setLlmProgress({ done: 0, total: all.length });
        // 3. reclassify (기존 runLlmReclassify 와 동일 로직이지만 상태맵에 바로 병합)
        const CHUNK = 100;
        const accum = new Map(llmResults);
        let done = 0;
        for (let i = 0; i < all.length; i += CHUNK) {
          const slice = all.slice(i, i + CHUNK);
          const r = await fetch("/api/training-samples/reclassify-batch", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              items: slice.map((p) => ({
                id: p.id,
                page: p.page,
                s3_key: p.s3_key,
                doc_type: p.doc_type || effectiveType,
              })),
              concurrency: effectiveModel === "harness-adviser" ? 4 : 10,
              model: effectiveModel,
              pass: llmPass,
              ...(customPrompt && customPrompt.trim().length > 50
                ? { customPrompt }
                : {}),
            }),
          });
          if (!r.ok) {
            const txt = await r.text();
            throw new Error(`HTTP ${r.status}: ${txt}`);
          }
          const data = await r.json();
          for (const res of data.results || [])
            accum.set(`${res.id}:${res.page}`, res);
          done += slice.length;
          setLlmProgress({ done, total: all.length });
          setLlmResults(new Map(accum));
        }
        alert(
          `완료: ${done.toLocaleString()}개 재검증됨. 현재 페이지 결과는 보이지만, 다른 페이지의 불일치는 DB 에 저장되었으며 각 페이지 탐색 시 반영됩니다.`,
        );
      } catch (e) {
        console.error("bulk reclassify fail", e);
        alert(`전체 재검증 실패: ${e.message}`);
      } finally {
        setLlmRunning(false);
      }
    },
    [
      selectedDocType,
      llmRunning,
      filterSource,
      confMinFilter,
      confSuspectOnly,
      llmModel,
      llmPass,
      customPrompt,
      llmResults,
    ],
  );

  // typePages 가 바뀌면 LLM 결과는 유지하되, 재검증 "의심만" 필터는 리셋하지 않음 (교차이동시 일관)
  // selectedDocType 이 바뀌면 결과맵 초기화 (다른 타입의 결과가 섞이지 않도록)
  useEffect(() => {
    setLlmResults(new Map());
    setLlmOnlyDisagree(false);
  }, [selectedDocType]);

  // ─── 타입별 건수 카운트 — 좌측 트리 배지
  //   - typeCounts: 전체 카운트 (항상 표시)
  //   - typeSuspectCounts: LLM 재검증에서 현재 doc_type 과 불일치한 페이지 (진짜 의심건 ⚠️)
  //   - typeCheckedCounts: LLM 재검증이 한 번이라도 돌아간 페이지 (검증 커버리지 🤖)
  const [typeSuspectCounts, setTypeSuspectCounts] = useState({});
  const [typeCheckedCounts, setTypeCheckedCounts] = useState({});
  const [typeCountsLoading, setTypeCountsLoading] = useState(false);
  const fetchTypeCounts = useCallback(async () => {
    setTypeCountsLoading(true);
    try {
      const base = new URLSearchParams();
      if (filterSource) base.append("source", filterSource);
      const mismatch = new URLSearchParams(base);
      mismatch.append("llm_mismatch_only", "1");
      const checked = new URLSearchParams(base);
      checked.append("llm_checked_only", "1");
      const [r1, r2, r3] = await Promise.all([
        fetch(`/api/training-samples/pages/by-doctype/counts?${base}`),
        fetch(`/api/training-samples/pages/by-doctype/counts?${mismatch}`),
        fetch(`/api/training-samples/pages/by-doctype/counts?${checked}`),
      ]);
      if (r1.ok) {
        const d = await r1.json();
        setTypeCounts(d.counts || {});
      } else setTypeCounts({});
      if (r2.ok) {
        const d = await r2.json();
        setTypeSuspectCounts(d.counts || {});
      } else setTypeSuspectCounts({});
      if (r3.ok) {
        const d = await r3.json();
        setTypeCheckedCounts(d.counts || {});
      } else setTypeCheckedCounts({});
    } catch (e) {
      console.error("type-counts fail", e);
      setTypeCounts({});
      setTypeSuspectCounts({});
      setTypeCheckedCounts({});
    } finally {
      setTypeCountsLoading(false);
    }
  }, [filterSource]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);
  useEffect(() => {
    if (leftPanelMode === "docType" || leftPanelMode === "mismatchGroups")
      fetchTypeCounts();
  }, [leftPanelMode, fetchTypeCounts]);
  useEffect(() => {
    if (leftPanelMode === "docType" && selectedDocType) {
      const offset = (typePage - 1) * typePageSize;
      fetchTypePages(selectedDocType, {
        conf_min: confMinFilter,
        llm_mismatch_only: confSuspectOnly,
        limit: typePageSize,
        offset,
      });
    }
  }, [
    leftPanelMode,
    selectedDocType,
    confMinFilter,
    confSuspectOnly,
    typePage,
    typePageSize,
    fetchTypePages,
  ]);

  // 불일치 그룹 모드: 선택 타입 바뀌면 자동 fetch + 좌측 트리도 의심카운트 우선 표시
  useEffect(() => {
    if (leftPanelMode === "mismatchGroups") {
      setConfSuspectOnly(true); // 좌측 트리에서 nSuspect 우선 표시되도록
      if (selectedDocType) {
        fetchMismatchGroups(selectedDocType);
        setMismatchExpandedGroup(null);
        setMismatchPreviewPage(null); // 타입 바뀌면 프리뷰도 리셋
      }
    } else if (leftPanelMode !== "mismatchGroups") {
      setMismatchGroupsData(null);
      setMismatchExpandedGroup(null);
      setMismatchPreviewPage(null);
    }
  }, [leftPanelMode, selectedDocType, fetchMismatchGroups, filterSource]);

  // 타입/신뢰도/소스 변경 시 1페이지로 리셋 + 선택 해제
  useEffect(() => {
    setTypePage(1);
    setTypeSelection(new Set());
    setTypePreviewPage(null);
  }, [selectedDocType, confMinFilter, confSuspectOnly, filterSource]);

  // 필터/정렬/검색 변경 시 항상 1페이지로 이동 (pageSize 변동 없음)
  useEffect(() => {
    setPage(1);
  }, [
    filterSource,
    filterQuality,
    filterVerified,
    filterDocType,
    searchQ,
    sortBy,
    dateFrom,
    dateTo,
    filterBl,
    filterImporter,
  ]);

  // 벌크 액션 실행 (fetchList/fetchStats 정의 이후에 선언)
  const runBulk = useCallback(
    async (action, payload) => {
      if (selectedIds.size === 0) return;
      const ids = Array.from(selectedIds);
      if (!window.confirm(`선택된 ${ids.length}건에 "${action}" 적용할까요?`))
        return;
      setBulkBusy(true);
      try {
        const res = await fetch("/api/training-samples/bulk", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ids, action, payload, verified_by: "user" }),
        });
        const json = await res.json();
        if (!res.ok || !json.success)
          throw new Error(json.error || "벌크 실패");
        clearSelection();
        await fetchList();
        await fetchStats();
      } catch (e) {
        alert(`벌크 실패: ${e.message}`);
      } finally {
        setBulkBusy(false);
      }
    },
    [selectedIds, clearSelection, fetchList, fetchStats],
  );

  // 필터 변경 시 1페이지로 reset + 선택 초기화 (실수 벌크 방지)
  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
  }, [
    filterSource,
    filterQuality,
    filterVerified,
    filterDocType,
    searchQ,
    sortBy,
  ]);

  // ─── 상세 조회 ────────────────────────────────────────────────────
  const loadDetail = useCallback(async (id) => {
    setSelectedId(id);
    setSelectedDetail(null);
    setEditedData(null);
    setImageUrl(null);
    setPagesList([]);
    setCurrentPageIdx(0);
    try {
      const [detailRes, pagesRes] = await Promise.all([
        fetch(`/api/training-samples/${id}`).then((r) => r.json()),
        fetch(`/api/training-samples/${id}/pages`)
          .then((r) => r.json())
          .catch(() => ({ pages: [] })),
      ]);
      setSelectedDetail(detailRes);
      setEditedData(JSON.parse(JSON.stringify(detailRes.verified_data || {})));
      const pages = Array.isArray(pagesRes?.pages) ? pagesRes.pages : [];
      if (pages.length > 0) {
        setPagesList(pages);
        // primary_page가 있으면 그걸 기본 표시 — 없으면 첫 페이지
        const primary = pagesRes.primary_page;
        const idx = primary
          ? Math.max(
              0,
              pages.findIndex((p) => p.page === primary),
            )
          : 0;
        setCurrentPageIdx(idx);
        if (pages[idx]?.url) setImageUrl(pages[idx].url);
      } else {
        // fallback: 단일 이미지
        const imgRes = await fetch(`/api/training-samples/${id}/image`)
          .then((r) => r.json())
          .catch(() => ({}));
        if (imgRes.url) setImageUrl(imgRes.url);
      }
    } catch (e) {
      console.error("상세 조회 실패", e);
    }
  }, []);

  // ─── 편집 핸들러 ──────────────────────────────────────────────────
  const updateHeaderField = (field, value) => {
    setEditedData((prev) => ({ ...prev, [field]: value }));
  };
  const updateItemField = (idx, field, value) => {
    setEditedData((prev) => {
      const items = [...(prev?.items || [])];
      items[idx] = { ...items[idx], [field]: value };
      return { ...prev, items };
    });
  };
  const addItem = () => {
    setEditedData((prev) => {
      const blank = Object.fromEntries(ITEM_FIELDS.map((f) => [f, ""]));
      return { ...prev, items: [...(prev?.items || []), blank] };
    });
  };
  const removeItem = (idx) => {
    setEditedData((prev) => {
      const items = [...(prev?.items || [])];
      items.splice(idx, 1);
      return { ...prev, items };
    });
  };
  const resetEdits = () => {
    if (selectedDetail)
      setEditedData(
        JSON.parse(JSON.stringify(selectedDetail.verified_data || {})),
      );
  };

  // ─── 저장 ───────────────────────────────────────────────────────
  const saveAsVerified = async () => {
    if (!selectedId || !editedData) return;
    setSaving(true);
    try {
      // draft에서 가져온 값이 있으면 provenance 플래그 동봉
      const body = { verified_data: editedData, verified_by: "user" };
      if (
        draftApplied &&
        (draftApplied.appliedItems ||
          (draftApplied.appliedHeaders || []).length > 0)
      ) {
        body.extraction_meta_patch = {
          source_override: {
            model: draftApplied.model,
            template_name: draftApplied.templateName,
            applied_headers: draftApplied.appliedHeaders || [],
            applied_items: !!draftApplied.appliedItems,
            applied_at: new Date().toISOString(),
          },
        };
      }
      const r = await fetch(`/api/training-samples/${selectedId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error("save failed");
      await fetchList();
      await fetchStats();
      await loadDetail(selectedId);
      setDraftApplied(null);
      alert("✅ human_verified로 저장되었습니다.");
    } catch (e) {
      alert("❌ 저장 실패: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const rejectSample = async () => {
    if (!selectedId) return;
    const reason = prompt("보류/제외 사유 (옵션)", "");
    if (reason === null) return;
    setSaving(true);
    try {
      const r = await fetch(`/api/training-samples/${selectedId}/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason, verified_by: "user" }),
      });
      if (!r.ok) throw new Error("reject failed");
      await fetchList();
      await fetchStats();
    } catch (e) {
      alert("❌ 보류 실패: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  // ─── 키보드 단축키 ──────────────────────────────────────────────
  // j/↓ next · k/↑ prev · v verify · x reject · space toggle-select · ? help
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (
        tag === "input" ||
        tag === "textarea" ||
        tag === "select" ||
        e.target?.isContentEditable
      )
        return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      const idx = items.findIndex((x) => x.id === selectedId);
      const moveTo = (newIdx) => {
        if (newIdx < 0 || newIdx >= items.length) return;
        const target = items[newIdx];
        if (target) loadDetail(target.id);
      };

      switch (e.key) {
        case "j":
        case "ArrowDown":
          e.preventDefault();
          moveTo(idx < 0 ? 0 : idx + 1);
          break;
        case "k":
        case "ArrowUp":
          e.preventDefault();
          moveTo(idx < 0 ? 0 : idx - 1);
          break;
        case "v":
          if (selectedId && editedData && !saving) {
            e.preventDefault();
            saveAsVerified();
          }
          break;
        case "x":
          if (selectedId && !saving) {
            e.preventDefault();
            rejectSample();
          }
          break;
        case " ":
          if (selectedId) {
            e.preventDefault();
            toggleSelect(selectedId);
          }
          break;
        case "?":
          e.preventDefault();
          setShowShortcuts((s) => !s);
          break;
        default:
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [items, selectedId, editedData, saving, toggleSelect]);

  // 🔧 RichInvoiceGrid 용 columns 정의 (shipping 기본 너비 기준)
  const ITEM_COLUMN_DEFS = useMemo(() => {
    const FIELD_WIDTHS = {
      Partno: 80,
      Description: 140,
      Composition: 60,
      HSCode: 75,
      "Unit Quantity": 60,
      UnitType: 50,
      Price: 70,
      Amount: 80,
      Netweight: 75,
      COO: 50,
      Incoterms: 70,
    };
    const NUMERIC = new Set(["Unit Quantity", "Price", "Amount", "Netweight"]);
    const base = ITEM_FIELDS.map((f) => {
      const col = {
        dataField: f,
        headerText: f,
        width: FIELD_WIDTHS[f] || 80,
        numeric: NUMERIC.has(f),
      };
      if (f === "HSCode") {
        // AI 자동분류 suggestion 시각화 — provenance 따라 tint
        col.styleFunction = function (
          rowIndex,
          columnIndex,
          value,
          headerText,
          item,
        ) {
          const sug = item?._aiSuggestion;
          const prov = item?._provenance;
          if (prov === "user_confirmed") return "rig-tint-user-confirmed";
          if (prov === "ai_suggested") return "rig-tint-ai-confirmed";
          if (sug && !value) return "rig-tint-suggested";
          return "";
        };
      }
      return col;
    });
    // 신뢰도 dot column — AI suggestion 의 confidence 시각화 + 클릭 시 modal 진입
    const confidenceCol = {
      dataField: "_confidenceDot",
      headerText: "AI",
      width: 36,
      editable: false,
      renderer: { type: "TemplateRenderer" },
      labelFunction: function (rowIndex, columnIndex, value, headerText, item) {
        const c = item?._aiSuggestion?.confidence;
        if (!c) return "";
        const color =
          c === "high" ? "#16a34a" : c === "medium" ? "#eab308" : "#ef4444";
        const title =
          c === "high" ? "HIGH 신뢰" : c === "medium" ? "MEDIUM" : "LOW";
        return `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${color}" title="${title} — 행 클릭 → AI 분류 근거 보기"></span>`;
      },
      style: "center-align",
    };
    // HSCode 바로 뒤에 confidence column 삽입
    const hsIdx = base.findIndex((c) => c.dataField === "HSCode");
    if (hsIdx >= 0) {
      base.splice(hsIdx + 1, 0, confidenceCol);
    } else {
      base.push(confidenceCol);
    }
    return base;
  }, []);

  // RichInvoiceGrid onChange → editedData.items 동기화
  // 사용자가 HSCode cell 을 직접 편집하면 _provenance='user_manual' 로 승격 (suggestion 메타는 유지)
  const handleGridChange = useCallback((rows) => {
    setEditedData((prev) => {
      const prevItems = prev?.items || [];
      const nextItems = rows.map((r, i) => {
        const before = prevItems[i] || {};
        const beforeHs = (before.HSCode || "").toString().trim();
        const nowHs = (r.HSCode || "").toString().trim();
        if (nowHs && nowHs !== beforeHs && r._provenance !== "user_confirmed") {
          return { ...r, _provenance: "user_manual" };
        }
        return r;
      });
      return { ...prev, items: nextItems };
    });
  }, []);

  // AI suggestion column 또는 HSCode cell 클릭 → modal 진입
  const handleCellClick = useCallback((event) => {
    if (!event) return;
    const { dataField, rowIndex, item } = event;
    if (dataField !== "_confidenceDot" && dataField !== "HSCode") return;
    // _confidenceDot 클릭은 무조건 modal — HSCode 클릭은 _aiSuggestion 있을 때만 (편집 방해 X)
    if (dataField === "HSCode" && !item?._aiSuggestion) return;
    setEvidenceModal({
      rowIndex,
      description: item?.Description || "",
      composition: item?.Composition || "",
    });
  }, []);

  // Modal 확정 callback — HSCode + provenance='user_confirmed' 로 update
  const handleEvidenceConfirm = useCallback((rowIndex, hsk, detail) => {
    setEditedData((prev) => {
      const items = [...(prev?.items || [])];
      if (rowIndex < 0 || rowIndex >= items.length) return prev;
      const next = {
        ...items[rowIndex],
        HSCode: hsk,
        _provenance: "user_confirmed",
      };
      // suggestion 메타 갱신 (사용자 확정 시점의 decided 정보 보존)
      if (detail?.decided) {
        next._aiSuggestion = {
          ...(items[rowIndex]._aiSuggestion || {}),
          confirmed_hsk: hsk,
          decided: detail.decided,
        };
      }
      items[rowIndex] = next;
      return { ...prev, items };
    });
  }, []);

  // 툴바의 행 추가/삭제 버튼 — 외부에서 직접 변경
  const addItemGrid = () => {
    const blank = Object.fromEntries(ITEM_FIELDS.map((f) => [f, ""]));
    setEditedData((prev) => ({
      ...prev,
      items: [...(prev?.items || []), blank],
    }));
  };
  const removeItemGrid = () => {
    alert(
      '헤더를 우클릭하면 "빈 행 삭제", 행을 우클릭하면 "선택 행 삭제" 메뉴를 사용할 수 있습니다.\n또는 Ctrl+-/- 단축키를 이용하세요.',
    );
  };

  // ─── 렌더링 ──────────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // ─── 툴바 렌더 ────────────────────────────────────────────────────
  const renderToolbar = () => (
    <div className="flex items-center gap-1.5 px-3 py-1.5 border-b border-gray-200 bg-white flex-shrink-0">
      {/* 재추출 (딥러닝) 버튼 */}
      <button
        onClick={reExtract}
        disabled={!selectedId || isReExtracting}
        className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold rounded transition-all"
        style={{
          backgroundColor:
            !selectedId || isReExtracting ? "#E5E7EB" : "#1F2937",
          color: !selectedId || isReExtracting ? "#9CA3AF" : "#FFFFFF",
          cursor: !selectedId || isReExtracting ? "not-allowed" : "pointer",
          border:
            "1px solid " +
            (!selectedId || isReExtracting ? "#D1D5DB" : "#111827"),
        }}
        title="선택한 샘플을 현재 템플릿으로 재추출"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M5 3 L5 21 L12 21 C17.5 21 21 17 21 12 C21 7 17.5 3 12 3 Z" />
          <line x1="8.5" y1="9" x2="14" y2="9" strokeOpacity="0.65" />
          <line x1="8.5" y1="12" x2="15.5" y2="12" strokeOpacity="0.65" />
          <line x1="8.5" y1="15" x2="13" y2="15" strokeOpacity="0.65" />
        </svg>
        {isReExtracting ? "추출 중..." : "딥러닝"}
      </button>

      {/* 검증 체크박스 + 모드 드롭다운 */}
      <div
        className="relative flex items-center gap-1 ml-1"
        ref={verifyDropdownRef}
      >
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={verifyEnabled}
            onChange={(e) => setVerifyEnabled(e.target.checked)}
            className="mono-checkbox"
          />
        </label>
        <button
          onClick={() =>
            verifyEnabled
              ? setShowVerifyDropdown((v) => !v)
              : setVerifyEnabled(true)
          }
          className={`flex items-center gap-0.5 text-[12px] font-medium ${verifyEnabled ? "text-[#1f1f1f]" : "text-[#5f6368]"} hover:text-[#1f1f1f]`}
          title="재추출 결과를 저장된 GT와 교차검증"
        >
          <span>
            {verifyEnabled
              ? verificationMode === "sequential"
                ? "정밀 검증"
                : "빠른 비교"
              : "검증"}
          </span>
          {verifyEnabled && <ChevronDown className="w-3 h-3 opacity-40" />}
        </button>
        {showVerifyDropdown && verifyEnabled && (
          <div
            className="absolute top-full left-0 mt-1 bg-white rounded-lg border border-[#dadce0] py-1 z-50 min-w-[110px]"
            style={{ boxShadow: "0 2px 8px rgba(60,64,67,0.15)" }}
          >
            {[
              { id: "parallel", label: "빠른 비교" },
              { id: "sequential", label: "정밀 검증" },
            ].map((m) => (
              <button
                key={m.id}
                onClick={() => {
                  setVerificationMode(m.id);
                  setShowVerifyDropdown(false);
                }}
                className={`w-full text-left px-3 py-1.5 text-[12px] ${verificationMode === m.id ? "bg-[#e8eaed] font-semibold text-[#1f1f1f]" : "text-[#444746] hover:bg-[#f1f3f4]"}`}
              >
                {m.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="w-px h-5 bg-gray-300 mx-0.5" />

      {/* 템플릿 선택 */}
      <select
        value={selectedTemplateId}
        onChange={(e) => setSelectedTemplateId(e.target.value)}
        className="text-[13px] border border-gray-300 rounded px-2 py-1 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 max-w-[200px] truncate"
        title="추출에 사용할 템플릿"
      >
        {templates.length === 0 && <option value="">템플릿 없음</option>}
        {templates.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
            {localStorage.getItem("docuai_default_template") === t.name
              ? " *"
              : ""}
          </option>
        ))}
      </select>

      {/* ★ 기본 템플릿 pin */}
      <button
        onClick={() => {
          if (!selectedTemplate) return;
          const cur = localStorage.getItem("docuai_default_template");
          if (cur === selectedTemplate.name) {
            localStorage.removeItem("docuai_default_template");
            alert(`기본 템플릿 해제: ${selectedTemplate.name}`);
          } else {
            localStorage.setItem(
              "docuai_default_template",
              selectedTemplate.name,
            );
            alert(`기본 템플릿 설정: ${selectedTemplate.name}`);
          }
          setTemplates([...templates]); // rerender
        }}
        className={`p-1 rounded ${
          selectedTemplate &&
          localStorage.getItem("docuai_default_template") ===
            selectedTemplate.name
            ? "text-gray-800 bg-gray-200"
            : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
        }`}
        title="현재 템플릿을 기본값으로"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill={
            selectedTemplate &&
            localStorage.getItem("docuai_default_template") ===
              selectedTemplate.name
              ? "currentColor"
              : "none"
          }
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      </button>

      {/* 템플릿관리 (스텁 — 다음 단계) */}
      <button
        onClick={() =>
          alert("템플릿관리는 메인 딥러닝 화면에서 편집 후 여기로 돌아오세요.")
        }
        className="flex items-center gap-1 px-1.5 py-0.5 rounded border border-transparent text-[#5f6368] hover:text-[#1f1f1f] hover:bg-[#f1f3f4] hover:border-[#dadce0]"
        title="템플릿 관리"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
        <span className="text-xs font-medium">템플릿관리</span>
      </button>

      {/* 프롬프트 (스텁 — 다음 단계) */}
      <button
        onClick={() => alert("프롬프트 편집은 다음 단계에서 연결됩니다.")}
        className="px-2 py-1 text-xs font-medium rounded border border-transparent text-[#5f6368] hover:text-[#1f1f1f] hover:bg-[#f1f3f4] hover:border-[#dadce0]"
        title="현재 템플릿 프롬프트 편집"
      >
        프롬프트
      </button>

      <div className="w-px h-5 bg-gray-300 mx-0.5" />

      {/* 도구 드롭다운 */}
      <div className="relative" ref={toolsMenuRef}>
        <button
          onClick={() => setShowToolsMenu((v) => !v)}
          className={`flex items-center gap-1 px-2 py-1 text-[13px] rounded ${showToolsMenu ? "text-[#1f1f1f] bg-[#e8eaed]" : "text-[#5f6368] hover:bg-[#f1f3f4]"}`}
          title="도구"
        >
          <Wrench size={13} />
          도구
        </button>
        {showToolsMenu && (
          <div className="absolute top-full left-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
            <div className="px-3 py-1.5 text-xs font-semibold text-gray-900 border-b border-gray-100 mb-1">
              도구
            </div>
            <button
              onClick={() => {
                setShowToolsMenu(false);
                autoHsClassify();
              }}
              disabled={
                isHsClassifying ||
                !editedData?.items?.some((r) => r.Description)
              }
              className="w-full flex items-center gap-2.5 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              title="현재 그리드의 Description 컬럼으로 HS code 자동 분류"
            >
              <Sparkles size={14} className="text-[#1A73E8]" />
              {isHsClassifying ? "HS 분류 중..." : "HS 자동분류"}
            </button>
            {hsClassifyStats && (
              <div className="px-3 py-1.5 text-[10px] text-gray-500 border-t border-gray-100 mt-1">
                분류완료 {hsClassifyStats.success}/{hsClassifyStats.total} ·
                검토필요 {hsClassifyStats.requires_review}
                <br />
                high {hsClassifyStats.confidence_distribution?.high || 0} / mid{" "}
                {hsClassifyStats.confidence_distribution?.medium || 0} / low{" "}
                {hsClassifyStats.confidence_distribution?.low || 0}
              </div>
            )}
            <button
              onClick={() => {
                setShowToolsMenu(false);
                alert("OCR 보조는 다음 단계에서 연결됩니다.");
              }}
              className="w-full flex items-center gap-2.5 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
            >
              <Search size={14} className="text-[#3C4043]" />
              OCR 좌표 추출
            </button>
            <button
              onClick={() => {
                setShowToolsMenu(false);
                alert("클립보드 붙여넣기는 다음 단계에서 연결됩니다.");
              }}
              className="w-full flex items-center gap-2.5 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
            >
              <Clipboard size={14} className="text-[#3C4043]" />셀 일괄 붙여넣기
            </button>
          </div>
        )}
      </div>

      {/* 상태: draft 있음 */}
      {draftData && (
        <span className="ml-auto text-[11px] text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-0.5">
          draft 준비됨
        </span>
      )}
    </div>
  );

  return (
    <div className="flex flex-col w-full h-full bg-gray-50">
      {/* 키보드 단축키 도움말 */}
      {showShortcuts && (
        <div
          className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center"
          onClick={() => setShowShortcuts(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl p-6 w-[360px] border border-slate-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-sm font-medium text-slate-800 mb-3 flex items-center justify-between">
              <span>⌨ 키보드 단축키</span>
              <button
                onClick={() => setShowShortcuts(false)}
                className="text-slate-400 hover:text-slate-700"
              >
                ×
              </button>
            </div>
            <div className="space-y-1.5 text-[12px] text-slate-700">
              {[
                ["j / ↓", "다음 샘플"],
                ["k / ↑", "이전 샘플"],
                ["v", "검증완료 저장 (human_verified)"],
                ["x", "보류 (reject)"],
                ["Space", "현재 샘플 체크박스 토글"],
                ["?", "이 도움말 열기/닫기"],
              ].map(([k, d]) => (
                <div key={k} className="flex justify-between gap-3">
                  <kbd className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-[11px] font-mono text-slate-700 min-w-[60px] text-center">
                    {k}
                  </kbd>
                  <span className="flex-1 text-right text-slate-600">{d}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 text-[11px] text-slate-400">
              입력 필드에 포커스되어 있으면 단축키가 비활성화됩니다.
            </div>
          </div>
        </div>
      )}
      {renderToolbar()}
      <div className="flex flex-1 min-h-0">
        {/* ═══ LEFT: 필터 + 리스트 ═══ */}
        <div
          className="flex-shrink-0 border-r border-slate-200 bg-white flex flex-col relative"
          style={{ width: leftWidth + "px" }}
        >
          {/* 좌측 패널 우측 가장자리 리사이저 */}
          <div
            className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-blue-200 transition-colors z-20"
            onMouseDown={(e) => {
              e.preventDefault();
              setIsLeftResizing(true);
            }}
            onDoubleClick={() => {
              setLeftUserAdjusted(false);
              localStorage.removeItem("training_verify_left_width");
              setLeftPanelWidth(leftDefault);
            }}
            title="드래그하여 폭 조절 · 더블클릭으로 기본폭 복원"
          />
          {/* 모드 탭: 건별 / 타입별 */}
          <div className="flex items-center border-b border-slate-200 bg-white">
            {[
              { k: "sample", label: "건별 리스트", desc: "샘플 단위로 검증" },
              {
                k: "docType",
                label: "타입별 뷰",
                desc: "같은 타입 페이지를 교차 확인",
              },
              {
                k: "mismatchGroups",
                label: "🔀 불일치 그룹",
                desc: "LLM 제안 타입별로 묶어서 일괄 처리 (BL 안의 D/O·이메일 등)",
              },
            ].map((t) => (
              <button
                key={t.k}
                onClick={() => setLeftPanelMode(t.k)}
                title={t.desc}
                className={`flex-1 text-[12px] py-2 border-b-2 transition-colors ${
                  leftPanelMode === t.k
                    ? "border-blue-600 text-slate-900 font-medium bg-white"
                    : "border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* ── docType / mismatchGroups 모드: 33-class 트리 ── */}
          {(leftPanelMode === "docType" ||
            leftPanelMode === "mismatchGroups") && (
            <>
              <div className="px-3 py-2 border-b border-slate-100 bg-white">
                <select
                  value={filterSource}
                  onChange={(e) => setFilterSource(e.target.value)}
                  className="w-full text-[12px] border border-slate-200 rounded px-2 py-1 text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  title="소스 (타입별 뷰에도 적용)"
                >
                  <option value="critic_distillation">
                    critic_distillation
                  </option>
                  <option value="customs_declaration_gt">
                    customs_declaration_gt
                  </option>
                  <option value="user_extraction">user_extraction</option>
                  <option value="">전체 소스</option>
                </select>
                <div className="flex items-center gap-1 mt-1.5">
                  <span className="text-[10px] text-slate-400 w-14">
                    최소 conf
                  </span>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={confMinFilter}
                    onChange={(e) =>
                      setConfMinFilter(parseFloat(e.target.value))
                    }
                    className="flex-1 accent-blue-600"
                  />
                  <span className="text-[10px] font-mono text-slate-700 w-8 text-right">
                    {confMinFilter.toFixed(2)}
                  </span>
                </div>
                <label
                  className="flex items-center gap-1 mt-1 text-[10px] cursor-pointer select-none"
                  title="conf < 1.0 인 페이지만 — 초기 분류기가 망설인 것 (이메일·메모·손글씨 등 오분류 의심 많음)"
                >
                  <input
                    type="checkbox"
                    checked={confSuspectOnly}
                    onChange={(e) => setConfSuspectOnly(e.target.checked)}
                    className="accent-rose-600"
                  />
                  <span
                    className={
                      confSuspectOnly
                        ? "text-rose-700 font-semibold"
                        : "text-slate-500"
                    }
                  >
                    ⚠ LLM 불일치 건만 보기 (진짜 의심건)
                  </span>
                </label>
              </div>
              <div className="flex-1 overflow-y-auto text-[11px]">
                {DOC_GROUP_ORDER.map((gk) => {
                  const groupTypes = TYPE_CODES.filter(
                    (c) => groupOf(c) === gk,
                  );
                  const visibleTypes = groupTypes.filter((c) => {
                    const n = confSuspectOnly
                      ? typeSuspectCounts[c] || 0
                      : typeCounts[c] || 0;
                    return n > 0;
                  });
                  const groupCount = visibleTypes.reduce(
                    (s, c) =>
                      s +
                      (confSuspectOnly
                        ? typeSuspectCounts[c] || 0
                        : typeCounts[c] || 0),
                    0,
                  );
                  if (groupCount === 0) return null;
                  return (
                    <div key={gk} className="mb-0.5">
                      <div className="px-3 py-1 text-[10px] font-semibold text-slate-600 bg-slate-50 border-y border-slate-100 flex items-center gap-1">
                        <span className="truncate">
                          {GROUP_META[gk]?.label || gk}
                        </span>
                        <span
                          className={`ml-auto tabular-nums ${confSuspectOnly ? "text-rose-600 font-semibold" : "text-slate-400"}`}
                        >
                          {groupCount.toLocaleString()}
                        </span>
                      </div>
                      {visibleTypes.map((code) => {
                        const nTotal = typeCounts[code] || 0;
                        const nSuspect = typeSuspectCounts[code] || 0;
                        const nChecked = typeCheckedCounts[code] || 0;
                        const nShown = confSuspectOnly ? nSuspect : nTotal;
                        const active = selectedDocType === code;
                        return (
                          <div
                            key={code}
                            className={`flex items-center gap-0.5 border-l-2 ${
                              active
                                ? "bg-blue-50 border-blue-600"
                                : "border-transparent hover:bg-slate-50"
                            }`}
                          >
                            <button
                              onClick={() => setSelectedDocType(code)}
                              title={DOC_TYPES[code]?.label}
                              className={`flex-1 min-w-0 px-3 py-1.5 text-left flex items-center gap-1.5 ${active ? "text-slate-900 font-medium" : "text-slate-700"}`}
                            >
                              <span className="font-mono text-[10px] text-slate-500 w-14 flex-shrink-0 truncate">
                                {prefixOf(code)}
                              </span>
                              <span className="truncate flex-1 min-w-0">
                                {labelOf(code)}
                              </span>
                            </button>
                            {!confSuspectOnly && nChecked > 0 && (
                              <span
                                className="text-[10px] px-1.5 py-0.5 text-slate-500 tabular-nums flex-shrink-0"
                                title={`LLM 재검증 완료: ${nChecked.toLocaleString()}건 / 전체 ${nTotal.toLocaleString()}건 (${nTotal ? Math.round((100 * nChecked) / nTotal) : 0}%)`}
                              >
                                {nChecked.toLocaleString()}
                              </span>
                            )}
                            {!confSuspectOnly && nSuspect > 0 && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedDocType(code);
                                  setConfSuspectOnly(true);
                                  setTypeSelection(new Set());
                                }}
                                className="text-[10px] px-1.5 py-0.5 rounded text-rose-600 hover:bg-rose-50 cursor-pointer tabular-nums flex-shrink-0"
                                title={`클릭 → ${nSuspect.toLocaleString()}개 LLM 불일치 건만 보기 (이 타입으로 전환 + 의심필터 ON)`}
                              >
                                {nSuspect.toLocaleString()}
                              </button>
                            )}
                            <span
                              className={`tabular-nums text-[10px] px-2 flex-shrink-0 ${active ? "text-blue-700" : confSuspectOnly ? "text-rose-600 font-semibold" : "text-slate-400"}`}
                            >
                              {nShown.toLocaleString()}
                            </span>
                            {nSuspect > 0 && (
                              <button
                                className="px-1.5 py-1 text-[11px] hover:bg-slate-100 text-slate-500 hover:text-slate-800 rounded flex-shrink-0"
                                disabled={llmRunning}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedDocType(code);
                                  if (!confSuspectOnly)
                                    setConfSuspectOnly(true);
                                  // override 로 현재 클릭한 타입+의심필터 즉시 적용 (state 업데이트 대기 불필요)
                                  runLlmReclassifyAllInType({
                                    type: code,
                                    suspectOnly: true,
                                  });
                                }}
                                title={`${nSuspect.toLocaleString()}개 의심건 즉시 LLM 재검증`}
                              >
                                ⚡
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
                {Object.keys(typeCounts).length === 0 && (
                  <div className="p-3 text-slate-400">
                    {typeCountsLoading
                      ? "로딩 중..."
                      : filterSource
                        ? `소스 '${filterSource}' 에 페이지 레벨 데이터가 없습니다. 상단 소스를 바꿔보세요.`
                        : "데이터 없음"}
                  </div>
                )}
              </div>
            </>
          )}

          {/* 통계 헤더 — 클릭하면 (source+quality) 필터로 이동 */}
          {leftPanelMode === "sample" && stats && (
            <div className="px-3 py-2 border-b border-slate-200 bg-slate-50">
              <div className="text-[11px] text-slate-500 mb-1.5">
                전체{" "}
                <span className="text-slate-800 font-medium">
                  {stats.total.toLocaleString()}
                </span>
                건 · 검증완료{" "}
                <span className="text-slate-800 font-medium">
                  {stats.verified.toLocaleString()}
                </span>
                건
              </div>
              <div className="flex flex-wrap gap-1">
                {stats.breakdown.map((b, i) => {
                  const active =
                    filterSource === b.source &&
                    filterQuality === b.quality_label;
                  const dot = QUALITY_DOTS[b.quality_label] || "bg-slate-400";
                  return (
                    <button
                      key={i}
                      onClick={() => {
                        setFilterSource(b.source);
                        setFilterQuality(b.quality_label);
                        setActivePreset("");
                      }}
                      title={`${b.source} · ${b.quality_label}`}
                      className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border transition-colors ${
                        active
                          ? "bg-slate-800 border-slate-800 text-white"
                          : "bg-white border-slate-200 text-slate-700 hover:bg-slate-100"
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${active ? "bg-white/70" : dot}`}
                      />
                      <span className="font-medium">{b.quality_label}</span>
                      <span
                        className={active ? "text-slate-200" : "text-slate-500"}
                      >
                        {b.n.toLocaleString()}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {leftPanelMode === "sample" && (
            <>
              {/* 검색 — 항상 보이는 상단 입력 (파일명 / sampleId / #id) */}
              <div className="px-3 pt-2.5 pb-1.5 border-b border-slate-100 bg-white space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <div className="relative flex-1">
                    <Search
                      size={13}
                      className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                    />
                    <input
                      type="text"
                      placeholder="파일명 또는 sampleId 검색"
                      value={searchQ}
                      onChange={(e) => setSearchQ(e.target.value)}
                      className="w-full text-[12px] border border-slate-200 rounded pl-7 pr-7 py-1.5 text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400"
                    />
                    {searchQ && (
                      <button
                        onClick={() => setSearchQ("")}
                        className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 text-[14px] leading-none w-5 h-5 flex items-center justify-center"
                        title="검색어 지우기"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  {/* 리스트 모드 토글 */}
                  <div className="flex border border-slate-200 rounded overflow-hidden text-[11px]">
                    <button
                      onClick={() => setListMode("card")}
                      className={`px-2 py-1.5 ${listMode === "card" ? "bg-slate-800 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                      title="카드 보기"
                    >
                      ▤
                    </button>
                    <button
                      onClick={() => setListMode("grid")}
                      className={`px-2 py-1.5 border-l border-slate-200 ${listMode === "grid" ? "bg-slate-800 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                      title="그리드 보기 (넓은 폭)"
                    >
                      ▦
                    </button>
                  </div>
                </div>

                {/* 신고필증 GT 전용 필터 — 신고일 구간 / B/L / 수입자 */}
                {filterSource === "customs_declaration_gt" && (
                  <div className="space-y-1.5 pt-1.5 border-t border-slate-100">
                    <div className="flex items-center gap-1 text-[11px] text-slate-600">
                      <span className="text-slate-400 w-10">신고일</span>
                      <input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        className="flex-1 text-[11px] border border-slate-200 rounded px-1.5 py-1 text-slate-700 focus:outline-none focus:ring-1 focus:ring-slate-400"
                      />
                      <span className="text-slate-400">~</span>
                      <input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        className="flex-1 text-[11px] border border-slate-200 rounded px-1.5 py-1 text-slate-700 focus:outline-none focus:ring-1 focus:ring-slate-400"
                      />
                      {(dateFrom || dateTo) && (
                        <button
                          onClick={() => {
                            setDateFrom("");
                            setDateTo("");
                          }}
                          className="text-slate-400 hover:text-slate-700 text-[14px] leading-none w-5 h-5 flex items-center justify-center"
                          title="날짜 초기화"
                        >
                          ×
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-[11px] text-slate-400 w-10">
                        B/L
                      </span>
                      <input
                        type="text"
                        placeholder="B/L 번호"
                        value={filterBl}
                        onChange={(e) => setFilterBl(e.target.value)}
                        className="flex-1 text-[11px] border border-slate-200 rounded px-1.5 py-1 text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400"
                      />
                      <span className="text-[11px] text-slate-400 w-12 pl-2">
                        수입자
                      </span>
                      <input
                        type="text"
                        placeholder="수입자"
                        value={filterImporter}
                        onChange={(e) => setFilterImporter(e.target.value)}
                        className="flex-1 text-[11px] border border-slate-200 rounded px-1.5 py-1 text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400"
                      />
                    </div>
                    {/* 빠른 날짜 프리셋 */}
                    <div className="flex flex-wrap gap-1 pl-11">
                      {[
                        {
                          k: "jan26",
                          label: "2026 1월",
                          from: "2026-01-01",
                          to: "2026-01-31",
                        },
                        {
                          k: "dec25",
                          label: "2025 12월",
                          from: "2025-12-01",
                          to: "2025-12-31",
                        },
                        {
                          k: "y2026",
                          label: "2026 전체",
                          from: "2026-01-01",
                          to: "2026-12-31",
                        },
                      ].map((p) => (
                        <button
                          key={p.k}
                          onClick={() => {
                            setDateFrom(p.from);
                            setDateTo(p.to);
                          }}
                          className={`text-[10px] px-1.5 py-0.5 rounded border ${dateFrom === p.from && dateTo === p.to ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"}`}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* 퀵필터 프리셋 — AI Studio 스타일 */}
              <div className="px-3 py-2 border-b border-slate-200 bg-white space-y-2">
                <div className="flex flex-wrap gap-1">
                  {QUICK_PRESETS.map((p) => (
                    <button
                      key={p.key}
                      onClick={() => applyPreset(p.key)}
                      title={p.desc || p.label}
                      className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                        activePreset === p.key
                          ? "bg-slate-800 text-white border-slate-800"
                          : "bg-white text-slate-700 border-slate-200 hover:bg-slate-100 hover:border-slate-300"
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                  <button
                    onClick={() => setShowShortcuts((s) => !s)}
                    className="ml-auto text-[11px] px-2 py-1 rounded border border-slate-200 bg-white hover:bg-slate-100 text-slate-600"
                    title="키보드 단축키 (? 키)"
                  >
                    ⌨
                  </button>
                  <button
                    onClick={() => setFilterCollapsed((c) => !c)}
                    className="text-[11px] px-2 py-1 rounded border border-slate-200 bg-white hover:bg-slate-100 text-slate-600"
                    title="상세 필터 토글"
                  >
                    {filterCollapsed ? "필터 ▾" : "필터 ▴"}
                  </button>
                </div>
                {/* 활성 필터 칩 — 동일 톤 */}
                {(filterSource ||
                  filterQuality ||
                  filterVerified ||
                  filterDocType ||
                  searchQ ||
                  dateFrom ||
                  dateTo ||
                  filterBl ||
                  filterImporter) && (
                  <div className="flex flex-wrap gap-1 items-center">
                    <span className="text-[10px] text-slate-400 mr-0.5">
                      필터:
                    </span>
                    {(dateFrom || dateTo) && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-700 flex items-center gap-1">
                        📅 {dateFrom || "…"} ~ {dateTo || "…"}
                        <button
                          onClick={() => {
                            setDateFrom("");
                            setDateTo("");
                          }}
                          className="text-slate-400 hover:text-slate-700"
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {filterBl && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-700 flex items-center gap-1">
                        B/L: {filterBl}
                        <button
                          onClick={() => setFilterBl("")}
                          className="text-slate-400 hover:text-slate-700"
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {filterImporter && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-700 flex items-center gap-1">
                        수입자: {filterImporter}
                        <button
                          onClick={() => setFilterImporter("")}
                          className="text-slate-400 hover:text-slate-700"
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {filterSource && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-700 flex items-center gap-1">
                        {filterSource === "customs_declaration_gt"
                          ? "신고필증 GT"
                          : filterSource}
                        <button
                          onClick={() => setFilterSource("")}
                          className="text-slate-400 hover:text-slate-700"
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {filterQuality && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-700 flex items-center gap-1">
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${QUALITY_DOTS[filterQuality] || "bg-slate-400"}`}
                        />
                        {filterQuality}
                        <button
                          onClick={() => {
                            setFilterQuality("");
                            setActivePreset("");
                          }}
                          className="text-slate-400 hover:text-slate-700"
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {filterVerified && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-700 flex items-center gap-1">
                        {filterVerified === "true" ? "검증됨" : "미검증"}
                        <button
                          onClick={() => {
                            setFilterVerified("");
                            setActivePreset("");
                          }}
                          className="text-slate-400 hover:text-slate-700"
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {filterDocType && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-700 flex items-center gap-1">
                        {filterDocType.split(" ")[0]}
                        <button
                          onClick={() => setFilterDocType("")}
                          className="text-slate-400 hover:text-slate-700"
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {searchQ && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-700 flex items-center gap-1">
                        "{searchQ}"
                        <button
                          onClick={() => setSearchQ("")}
                          className="text-slate-400 hover:text-slate-700"
                        >
                          ×
                        </button>
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* 상세 필터 (접힘 가능) */}
              {!filterCollapsed && (
                <div className="px-3 py-2 space-y-2 border-b border-slate-200 bg-white">
                  <div className="flex gap-1">
                    <select
                      value={filterSource}
                      onChange={(e) => setFilterSource(e.target.value)}
                      className="flex-1 text-xs border border-slate-200 rounded px-2 py-1 text-slate-700 focus:outline-none focus:ring-1 focus:ring-slate-400"
                    >
                      <option value="critic_distillation">
                        critic_distillation
                      </option>
                      <option value="customs_declaration_gt">
                        customs_declaration_gt (신고필증)
                      </option>
                      <option value="user_extraction">user_extraction</option>
                      <option value="">전체 소스</option>
                    </select>
                  </div>
                  <div className="flex gap-1">
                    <select
                      value={filterQuality}
                      onChange={(e) => {
                        setFilterQuality(e.target.value);
                        setActivePreset("");
                      }}
                      className="flex-1 text-xs border border-slate-200 rounded px-2 py-1 text-slate-700 focus:outline-none focus:ring-1 focus:ring-slate-400"
                    >
                      <option value="">전체 품질</option>
                      <option value="gold">gold</option>
                      <option value="silver">silver</option>
                      <option value="needs_human">needs_human</option>
                      <option value="human_verified">human_verified</option>
                      <option value="rejected">rejected</option>
                    </select>
                    <select
                      value={filterVerified}
                      onChange={(e) => {
                        setFilterVerified(e.target.value);
                        setActivePreset("");
                      }}
                      className="flex-1 text-xs border border-slate-200 rounded px-2 py-1 text-slate-700 focus:outline-none focus:ring-1 focus:ring-slate-400"
                    >
                      <option value="">검증여부</option>
                      <option value="true">검증됨</option>
                      <option value="false">미검증</option>
                    </select>
                  </div>
                  <div className="flex gap-1">
                    <select
                      value={filterDocType}
                      onChange={(e) => setFilterDocType(e.target.value)}
                      className="flex-1 text-xs border border-slate-200 rounded px-2 py-1 text-slate-700 focus:outline-none focus:ring-1 focus:ring-slate-400"
                    >
                      <option value="">전체 문서유형</option>
                      <option value="Commercial Invoice">
                        Commercial Invoice
                      </option>
                      <option value="Bill of Lading">Bill of Lading</option>
                      <option value="Packing List">Packing List</option>
                      <option value="Certificate of Origin">
                        Certificate of Origin
                      </option>
                      <option value="Mixed (CI+PL)">Mixed</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="flex gap-1">
                    <select
                      value={sortBy}
                      onChange={(e) => {
                        setSortBy(e.target.value);
                        setActivePreset("");
                      }}
                      className="flex-1 text-xs border border-slate-200 rounded px-2 py-1 text-slate-700 focus:outline-none focus:ring-1 focus:ring-slate-400"
                    >
                      <option value="quality_label">
                        품질순 (needs_human 먼저)
                      </option>
                      <option value="confidence_asc">신뢰도 낮은순</option>
                      <option value="created_at_desc">최신순</option>
                      <option value="created_at_asc">오래된순</option>
                      <option value="declaration_date_asc">
                        신고일 오름차순
                      </option>
                      <option value="declaration_date_desc">
                        신고일 내림차순
                      </option>
                    </select>
                  </div>
                </div>
              )}

              {/* 전체선택 바 */}
              {items.length > 0 && (
                <div className="px-3 py-1.5 border-b border-slate-200 bg-white flex items-center gap-2 text-[11px]">
                  <input
                    type="checkbox"
                    checked={
                      items.length > 0 &&
                      items.every((it) => selectedIds.has(it.id))
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedIds(new Set(items.map((it) => it.id)));
                      } else {
                        clearSelection();
                      }
                    }}
                    className="cursor-pointer accent-slate-700"
                    title="현재 페이지 전체선택"
                  />
                  <span className="text-slate-600">
                    {selectedIds.size > 0
                      ? `${selectedIds.size}건 선택`
                      : `${items.length}건 표시`}
                  </span>
                  {selectedIds.size > 0 && (
                    <button
                      onClick={clearSelection}
                      className="ml-auto text-[10px] text-slate-500 hover:text-slate-800"
                    >
                      선택해제
                    </button>
                  )}
                </div>
              )}

              {/* 리스트 */}
              <div className="flex-1 overflow-y-auto">
                {loading && (
                  <div className="p-3 text-xs text-slate-500">로딩 중...</div>
                )}
                {!loading && items.length === 0 && (
                  <div className="p-3 text-xs text-slate-500">결과 없음</div>
                )}

                {/* 그리드 모드 헤더 */}
                {listMode === "grid" && items.length > 0 && (
                  <div className="sticky top-0 z-10 bg-slate-50 border-b border-slate-200 text-[10px] text-slate-500 font-medium flex items-center px-3 py-1.5 gap-2">
                    <span className="w-4" />
                    <span className="w-3" />
                    <span className="w-[72px]">신고일</span>
                    <span className="w-[120px]">B/L</span>
                    <span className="w-[120px] truncate">수입자</span>
                    <span className="flex-1 truncate">파일명</span>
                    <span className="w-12 text-right">품명</span>
                    <span className="w-10 text-right">란</span>
                    <span className="w-10 text-right">conf</span>
                    <span className="w-10 text-right">#id</span>
                  </div>
                )}

                {items.map((it) => {
                  const checked = selectedIds.has(it.id);

                  // ── 그리드 모드: 컴팩트 단일 행
                  if (listMode === "grid") {
                    return (
                      <div
                        key={it.id}
                        className={`px-3 py-1.5 border-b border-slate-100 hover:bg-slate-50 flex items-center gap-2 text-[11px] transition-colors cursor-pointer ${selectedId === it.id ? "bg-slate-100 border-l-2 border-l-slate-800" : "border-l-2 border-l-transparent"} ${checked && selectedId !== it.id ? "bg-slate-50" : ""}`}
                        onClick={() => loadDetail(it.id)}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSelect(it.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 cursor-pointer accent-slate-700 flex-shrink-0"
                        />
                        <span
                          className={`w-3 h-1.5 w-1.5 rounded-full flex-shrink-0 ${QUALITY_DOTS[it.quality_label] || "bg-slate-400"}`}
                          title={it.quality_label}
                        />
                        <span className="w-[72px] text-slate-700 font-mono text-[10.5px] flex-shrink-0">
                          {it.declaration_date
                            ? it.declaration_date.slice(5)
                            : "—"}
                        </span>
                        <span
                          className="w-[120px] text-slate-700 font-mono text-[10.5px] truncate flex-shrink-0"
                          title={it.bl_number || ""}
                        >
                          {it.bl_number || "—"}
                        </span>
                        <span
                          className="w-[120px] text-slate-700 truncate flex-shrink-0"
                          title={it.importer || ""}
                        >
                          {it.importer || "—"}
                        </span>
                        <span
                          className="flex-1 text-slate-800 truncate min-w-0"
                          title={it.file_name}
                        >
                          {it.file_name}
                        </span>
                        <span className="w-12 text-right text-slate-600 flex-shrink-0">
                          {it.item_count ?? 0}
                        </span>
                        <span
                          className="w-10 text-right text-slate-500 flex-shrink-0"
                          title="imlan_rows"
                        >
                          {it.imlan_rows ?? "—"}
                        </span>
                        <span className="w-10 text-right text-slate-500 flex-shrink-0">
                          {parseFloat(it.confidence_score || 0).toFixed(2)}
                        </span>
                        <span className="w-10 text-right text-slate-400 flex-shrink-0 flex items-center justify-end gap-1">
                          {it.verified_at && (
                            <span
                              className="w-1.5 h-1.5 rounded-full bg-emerald-500"
                              title="검증완료"
                            />
                          )}
                          #{it.id}
                        </span>
                      </div>
                    );
                  }

                  // ── 카드 모드 (기존)
                  return (
                    <div
                      key={it.id}
                      className={`px-3 py-2 border-b border-slate-100 hover:bg-slate-50 flex gap-2 transition-colors ${selectedId === it.id ? "bg-slate-100 border-l-2 border-l-slate-800" : "border-l-2 border-l-transparent"} ${checked && selectedId !== it.id ? "bg-slate-50" : ""}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSelect(it.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-0.5 cursor-pointer accent-slate-700"
                      />
                      <div
                        className="flex-1 min-w-0 cursor-pointer"
                        onClick={() => loadDetail(it.id)}
                      >
                        <div className="flex items-center gap-1 mb-0.5">
                          {qualityBadge(it.quality_label)}
                          {it.document_type &&
                            it.document_type !== "Commercial Invoice" && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded border border-slate-200 bg-white text-slate-600">
                                {it.document_type.split(" ")[0]}
                              </span>
                            )}
                          {it.verified_at && (
                            <span
                              className="w-1.5 h-1.5 rounded-full bg-emerald-500"
                              title="검증완료"
                            />
                          )}
                          <span className="text-[10px] text-slate-400 ml-auto">
                            #{it.id}
                          </span>
                        </div>
                        <div
                          className="text-[11px] text-slate-800 truncate"
                          title={it.file_name}
                        >
                          {it.file_name}
                        </div>
                        <div className="text-[10px] text-slate-500 mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                          <span>
                            conf=
                            {parseFloat(it.confidence_score || 0).toFixed(2)}
                          </span>
                          <span>items={it.item_count}</span>
                          {it.declaration_date && (
                            <span className="text-slate-600">
                              📅 {it.declaration_date}
                            </span>
                          )}
                          {it.bl_number && (
                            <span className="font-mono" title="B/L">
                              B/L {it.bl_number}
                            </span>
                          )}
                          {it.importer && (
                            <span
                              className="truncate max-w-[140px]"
                              title={it.importer}
                            >
                              👤 {it.importer}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* 벌크 액션바 */}
              {selectedIds.size > 0 && (
                <div className="px-3 py-2 border-t border-slate-200 bg-slate-50 text-[11px] space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-800">
                      선택 {selectedIds.size}건
                    </span>
                    {bulkBusy && (
                      <span className="text-slate-500">처리중...</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    <select
                      disabled={bulkBusy}
                      defaultValue=""
                      onChange={(e) => {
                        const v = e.target.value;
                        e.target.value = "";
                        if (v)
                          runBulk("set_document_type", { document_type: v });
                      }}
                      className="text-[11px] border border-slate-200 rounded px-1.5 py-0.5 bg-white text-slate-700"
                    >
                      <option value="">문서유형 일괄변경...</option>
                      <option value="Commercial Invoice">
                        → Commercial Invoice
                      </option>
                      <option value="Bill of Lading">→ Bill of Lading</option>
                      <option value="Packing List">→ Packing List</option>
                      <option value="Certificate of Origin">
                        → Certificate of Origin
                      </option>
                      <option value="Mixed (CI+PL)">→ Mixed (CI+PL)</option>
                      <option value="Other">→ Other</option>
                    </select>
                    <button
                      disabled={bulkBusy}
                      onClick={() =>
                        runBulk("requalify", { quality_label: "needs_human" })
                      }
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border border-slate-200 bg-white text-slate-700 hover:bg-slate-100 disabled:opacity-40"
                      title="needs_human으로 되돌려 재검증 대상으로 표시"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                      검증대기로
                    </button>
                    <button
                      disabled={bulkBusy}
                      onClick={() => {
                        const reason =
                          window.prompt("보류 사유 (선택)", "") || "";
                        runBulk("reject", { reason });
                      }}
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border border-slate-200 bg-white text-slate-700 hover:bg-slate-100 disabled:opacity-40"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-stone-500" />
                      보류
                    </button>
                  </div>
                </div>
              )}

              {/* 페이징 */}
              <div className="px-3 py-2 border-t border-slate-200 bg-white flex items-center gap-1 text-xs text-slate-600">
                <button
                  onClick={() => setPage(1)}
                  disabled={page <= 1}
                  className="px-2 py-0.5 border border-slate-200 rounded hover:bg-slate-100 disabled:opacity-40"
                >
                  |&lt;
                </button>
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-2 py-0.5 border border-slate-200 rounded hover:bg-slate-100 disabled:opacity-40"
                >
                  &lt;
                </button>
                <span className="flex-1 text-center text-slate-700">
                  {page} / {totalPages}
                  <span className="text-slate-400 ml-1">
                    ({total.toLocaleString()}건)
                  </span>
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-2 py-0.5 border border-slate-200 rounded hover:bg-slate-100 disabled:opacity-40"
                >
                  &gt;
                </button>
                <button
                  onClick={() => setPage(totalPages)}
                  disabled={page >= totalPages}
                  className="px-2 py-0.5 border border-slate-200 rounded hover:bg-slate-100 disabled:opacity-40"
                >
                  &gt;|
                </button>
              </div>
            </>
          )}
        </div>

        {/* ═══ MIDDLE: 이미지 뷰어 / 타입별 그리드 ═══ */}
        <div className="flex-1 bg-gray-100 flex flex-col">
          {/* 타입별 뷰 — 썸네일 그리드 */}
          {leftPanelMode === "docType" && (
            <div className="flex-1 flex flex-col min-h-0">
              {!selectedDocType && (
                <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
                  왼쪽에서 문서 타입을 선택하세요
                </div>
              )}
              {selectedDocType &&
                (() => {
                  const groupKey = groupOf(selectedDocType);
                  const groupMeta = GROUP_META[groupKey] || { label: groupKey };
                  const totalTypePages = Math.max(
                    1,
                    Math.ceil(typeTotal / typePageSize),
                  );
                  const pageFrom = (typePage - 1) * typePageSize + 1;
                  const pageTo = Math.min(typeTotal, typePage * typePageSize);
                  const selCount = typeSelection.size;
                  const toggleSel = (key) =>
                    setTypeSelection((prev) => {
                      const n = new Set(prev);
                      n.has(key) ? n.delete(key) : n.add(key);
                      return n;
                    });
                  const keyOf = (p) => `${p.id}:${p.page}`;
                  const allChecked =
                    typePages.length > 0 &&
                    typePages.every((p) => typeSelection.has(keyOf(p)));
                  const toggleAll = () =>
                    setTypeSelection((prev) => {
                      if (allChecked) return new Set();
                      const n = new Set(prev);
                      typePages.forEach((p) => n.add(keyOf(p)));
                      return n;
                    });
                  const bulkReclassify = async (newType) => {
                    if (!newType || newType === selectedDocType) return;
                    if (
                      !window.confirm(
                        `선택된 ${selCount}개 페이지의 타입을 ${prefixOf(newType)}(${labelOf(newType)}) 로 변경할까요?`,
                      )
                    )
                      return;
                    const items = typePages.filter((p) =>
                      typeSelection.has(keyOf(p)),
                    );
                    for (const p of items) {
                      await updatePageDocType(p.id, p.page, newType);
                    }
                    setTypeSelection(new Set());
                  };

                  // ─── LLM 재검증 헬퍼 ────────────────────────────────
                  const llmResultOf = (p) => llmResults.get(keyOf(p));
                  const llmDone = typePages.filter(
                    (p) => llmResultOf(p) && !llmResultOf(p).error,
                  ).length;
                  const llmDisagreeList = typePages.filter((p) => {
                    const r = llmResultOf(p);
                    return r && !r.error && r.agrees === false;
                  });
                  const llmAgreeCount = typePages.filter((p) => {
                    const r = llmResultOf(p);
                    return r && !r.error && r.agrees === true;
                  }).length;
                  const visiblePages = llmOnlyDisagree
                    ? llmDisagreeList
                    : typePages;

                  // 선택이 있으면 선택된 것 중 불일치, 없으면 전체 불일치를 적용 대상으로
                  const llmApplyTargets =
                    selCount > 0
                      ? llmDisagreeList.filter((p) =>
                          typeSelection.has(keyOf(p)),
                        )
                      : llmDisagreeList;

                  const applyLlmSuggestions = async () => {
                    if (llmApplyTargets.length === 0) return;
                    const scope =
                      selCount > 0
                        ? `선택된 ${llmApplyTargets.length}건`
                        : `전체 불일치 ${llmApplyTargets.length}건`;
                    if (
                      !window.confirm(
                        `${scope} 의 타입을 LLM 제안값으로 변경합니다.\n각 페이지는 LLM 이 판단한 서로 다른 타입으로 변경됩니다. 진행할까요?`,
                      )
                    )
                      return;
                    for (const p of llmApplyTargets) {
                      const r = llmResultOf(p);
                      if (r && r.llm_type && r.llm_type !== p.doc_type) {
                        await updatePageDocType(p.id, p.page, r.llm_type);
                      }
                    }
                    setTypeSelection(new Set());
                  };

                  // LLM 불일치만 전체선택 + 화면도 불일치건만 필터링 (사용자 카드별 육안 검토용)
                  const selectAllDisagree = () => {
                    setTypeSelection(() => new Set(llmDisagreeList.map(keyOf)));
                    setLlmOnlyDisagree(true);
                  };
                  // LLM 일치건만 전체선택 (신뢰도 낮은것 등 빠른 확인용) — 화면은 유지
                  const selectAllAgree = () => {
                    const agrees = typePages.filter((p) => {
                      const r = llmResultOf(p);
                      return r && !r.error && r.agrees === true;
                    });
                    setTypeSelection(() => new Set(agrees.map(keyOf)));
                    setLlmOnlyDisagree(false);
                  };
                  return (
                    <>
                      {/* 그룹 카테고리 브레드크럼 + 선택된 타입 정보 */}
                      <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-[11px] text-slate-600 flex items-center gap-2 flex-wrap">
                        <span className="uppercase tracking-wider text-slate-400 font-semibold">
                          {groupKey}
                        </span>
                        <span className="text-slate-400">/</span>
                        <span className="font-medium text-slate-700">
                          {groupMeta.label}
                        </span>
                        <span className="text-slate-400">/</span>
                        <span className="font-mono text-slate-900">
                          {prefixOf(selectedDocType)}
                        </span>
                        <span className="text-slate-800 font-semibold">
                          {labelOf(selectedDocType)}
                        </span>
                        <span className="ml-auto text-slate-500 tabular-nums">
                          {typeTotal > 0
                            ? `${pageFrom.toLocaleString()}–${pageTo.toLocaleString()} / `
                            : ""}
                          {typeTotal.toLocaleString()}건
                        </span>
                      </div>

                      {/* LLM 재검증 툴바 */}
                      <div className="px-3 py-1.5 bg-white border-b border-slate-200 text-[11px] flex items-center gap-2 flex-wrap">
                        <button
                          className="px-2.5 py-0.5 bg-slate-900 text-white rounded-full hover:bg-slate-700 disabled:opacity-40 flex items-center gap-1"
                          disabled={llmRunning || typePages.length === 0}
                          onClick={() => {
                            const target =
                              selCount > 0
                                ? typePages.filter((p) =>
                                    typeSelection.has(keyOf(p)),
                                  )
                                : typePages;
                            if (
                              !window.confirm(
                                `${target.length}개 페이지를 LLM(${llmModel}) 로 재분류합니다.\n예상 소요: ~${Math.ceil((target.length / 10) * 2)}초 (병렬 10). 진행할까요?`,
                              )
                            )
                              return;
                            runLlmReclassify(target);
                          }}
                          title="현재 페이지(선택시 선택된 것만) 를 Gemini 로 재분류"
                        >
                          LLM 재검증
                          {selCount > 0
                            ? ` · ${selCount}`
                            : ` · ${typePages.length}`}
                        </button>
                        <button
                          className="px-2.5 py-0.5 border border-slate-300 text-slate-700 rounded-full bg-white hover:bg-slate-50 disabled:opacity-40 flex items-center gap-1"
                          disabled={
                            llmRunning || !selectedDocType || typeTotal === 0
                          }
                          onClick={runLlmReclassifyAllInType}
                          title={`현재 타입(${selectedDocType}) 전체 ${typeTotal.toLocaleString()}개 페이지를 일괄 재검증 — 결과는 DB 에 저장되며, 다른 페이지로 이동시 반영됩니다.`}
                        >
                          전체 타입 · {typeTotal.toLocaleString()}
                        </button>
                        <button
                          className={`px-2.5 py-0.5 rounded-full border flex items-center gap-1 ${customPrompt ? "bg-slate-100 border-slate-400 text-slate-800 hover:bg-slate-200" : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"}`}
                          onClick={openPromptEditor}
                          disabled={llmRunning}
                          title={
                            customPrompt
                              ? `커스텀 프롬프트 사용 중 (${customPrompt.length.toLocaleString()}자). 클릭해서 편집.`
                              : "분류 프롬프트 편집 — BL 에 이메일 섞인 케이스처럼 케이스별 지시어 추가 가능"
                          }
                        >
                          프롬프트{customPrompt ? " · 커스텀" : ""}
                        </button>
                        <select
                          className="border border-slate-300 rounded px-1.5 py-0.5 bg-white text-[11px] text-slate-700"
                          value={llmModel}
                          onChange={(e) => setLlmModel(e.target.value)}
                          disabled={llmRunning}
                          title="LLM 모델 선택"
                        >
                          <option value={GEMINI_FLASH_MODEL}>
                            Gemini 3.7 Flash (권장)
                          </option>
                          <option value="pro-adviser">
                            🔶 Pro Adviser (Flash→Pro · 같은 벤더·저비용)
                          </option>
                          <option value="hybrid-adviser">
                            🎯 Hybrid Adviser (Flash→Pro→Sonnet · 최적 밸런스)
                          </option>
                          <option value="harness-adviser">
                            🛡 Harness Adviser (Flash topK → Pro verifier ×
                            top-2 → Sonnet tie-break)
                          </option>
                          <option value="sonnet-adviser">
                            🎓 Sonnet Adviser (Flash→Sonnet · 대량 권장)
                          </option>
                          <option value="opus-adviser">
                            🧠 Opus Adviser (Flash→Opus · 최고 정확도)
                          </option>
                          <option value="claude-sonnet-4-6">
                            💠 Sonnet Direct
                          </option>
                          <option value="claude-opus-4-7">
                            🔮 Opus Direct (최고 정확도·느림)
                          </option>
                        </select>
                        <select
                          className="border border-slate-300 rounded px-1.5 py-0.5 bg-white text-[11px] text-slate-700"
                          value={llmPass}
                          onChange={(e) =>
                            setLlmPass(parseInt(e.target.value, 10))
                          }
                          disabled={llmRunning}
                          title="Pass 선택: 1=엄격(OTHER 허용) / 2=OTHER 재분류"
                        >
                          <option value={1}>Pass 1 (엄격)</option>
                          <option value={2}>Pass 2 (OTHER 재분류)</option>
                        </select>
                        {llmRunning && (
                          <span className="text-slate-600 tabular-nums">
                            {llmProgress.done} / {llmProgress.total}
                          </span>
                        )}
                        {llmDone > 0 && !llmRunning && (
                          <>
                            <span
                              className="text-slate-500"
                              title={`현재 페이지 ${typePages.length}건 중 LLM 재검증이 돌아간 건수입니다. 타입 전체 커버리지는 좌측 🤖 배지를 참고하세요.`}
                            >
                              이 페이지 {llmDone}/{typePages.length}
                            </span>
                            <span className="px-1.5 py-0.5 text-emerald-700 tabular-nums">
                              · 일치 {llmAgreeCount}
                            </span>
                            <span className="px-1.5 py-0.5 text-rose-700 tabular-nums">
                              · 불일치 {llmDisagreeList.length}
                            </span>
                            <label className="flex items-center gap-1 cursor-pointer ml-1 text-slate-600">
                              <input
                                type="checkbox"
                                checked={llmOnlyDisagree}
                                onChange={(e) =>
                                  setLlmOnlyDisagree(e.target.checked)
                                }
                              />
                              <span>불일치만 보기</span>
                            </label>
                            {llmDisagreeList.length > 0 && (
                              <button
                                className="px-2 py-0.5 border border-slate-300 rounded-full bg-white hover:bg-slate-50 text-slate-700"
                                onClick={selectAllDisagree}
                                title="LLM 불일치 건만 체크박스 선택 — 육안 확인 후 틀린 것만 빼고 적용하세요"
                              >
                                불일치 {llmDisagreeList.length} 선택
                              </button>
                            )}
                            {llmAgreeCount > 0 && (
                              <button
                                className="px-2 py-0.5 border border-slate-300 rounded-full bg-white hover:bg-slate-50 text-slate-700"
                                onClick={selectAllAgree}
                                title="LLM 일치 건만 선택"
                              >
                                일치 {llmAgreeCount} 선택
                              </button>
                            )}
                            {llmApplyTargets.length > 0 && (
                              <button
                                className="px-2.5 py-0.5 bg-slate-900 text-white rounded-full hover:bg-slate-700"
                                onClick={applyLlmSuggestions}
                                title={
                                  selCount > 0
                                    ? "선택된 불일치 건에 LLM 제안 적용"
                                    : "모든 불일치에 LLM 제안 적용"
                                }
                              >
                                LLM 적용 · {llmApplyTargets.length}
                              </button>
                            )}
                            <button
                              className="px-1.5 py-0.5 border border-slate-300 rounded bg-white hover:bg-slate-50"
                              onClick={() => {
                                setLlmResults(new Map());
                                setLlmOnlyDisagree(false);
                              }}
                              title="LLM 결과 초기화"
                            >
                              결과 지우기
                            </button>
                          </>
                        )}
                      </div>

                      {/* 필터 툴바 — 현재 필터 상태/토글 */}
                      <div className="px-3 py-1.5 bg-slate-50/60 border-b border-slate-200 text-[11px] text-slate-600 flex items-center gap-2 flex-wrap">
                        <span className="text-slate-400 tracking-wide text-[10px] uppercase flex-shrink-0">
                          필터
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setConfSuspectOnly((s) => !s);
                            setTypeSelection(new Set());
                          }}
                          className={`px-2.5 py-0.5 rounded-full border transition-colors ${confSuspectOnly ? "bg-slate-900 text-white border-slate-900 hover:bg-slate-700" : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"}`}
                          title="LLM 이 다른 타입으로 판단한 불일치(의심) 건만 보기"
                        >
                          LLM 불일치만
                          {typeSuspectCounts[selectedDocType]
                            ? ` · ${typeSuspectCounts[selectedDocType].toLocaleString()}`
                            : ""}
                        </button>
                        <button
                          type="button"
                          onClick={() => setLlmOnlyDisagree((s) => !s)}
                          disabled={llmDone === 0}
                          className={`px-2.5 py-0.5 rounded-full border transition-colors ${llmOnlyDisagree ? "bg-slate-900 text-white border-slate-900 hover:bg-slate-700" : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"}`}
                          title={
                            llmDone === 0
                              ? "LLM 재검증 실행 후 사용 가능"
                              : "현재 페이지에서 LLM 불일치 건만 보기 (재검증 실행된 페이지 중)"
                          }
                        >
                          이 페이지 불일치 · {llmDisagreeList.length}
                        </button>
                        <label
                          className="flex items-center gap-1 cursor-pointer px-2.5 py-0.5 border border-slate-300 rounded-full bg-white"
                          title="카드 신뢰도 기준값 이하 페이지 제외"
                        >
                          <span className="text-slate-500">신뢰도 ≥</span>
                          <input
                            type="number"
                            min="0"
                            max="1"
                            step="0.05"
                            value={confMinFilter}
                            onChange={(e) =>
                              setConfMinFilter(
                                Math.max(
                                  0,
                                  Math.min(1, parseFloat(e.target.value) || 0),
                                ),
                              )
                            }
                            className="w-11 tabular-nums text-center border-0 p-0 bg-transparent focus:outline-none"
                          />
                        </label>
                        {(confSuspectOnly ||
                          llmOnlyDisagree ||
                          confMinFilter > 0) && (
                          <button
                            type="button"
                            onClick={() => {
                              setConfSuspectOnly(false);
                              setLlmOnlyDisagree(false);
                              setConfMinFilter(0);
                            }}
                            className="px-2 py-0.5 text-slate-500 hover:text-slate-800"
                            title="모든 필터 해제"
                          >
                            해제
                          </button>
                        )}
                        <span
                          className="ml-auto text-slate-400 tabular-nums"
                          title="필터 적용 후 표시 건수 / 전체"
                        >
                          {visiblePages.length.toLocaleString()} /{" "}
                          {typeTotal.toLocaleString()}
                        </span>
                      </div>

                      {/* 툴바: 페이지 크기 / 선택 / 벌크액션 */}
                      <div className="px-3 py-1.5 bg-white border-b border-slate-200 text-[11px] text-slate-600 flex items-center gap-2 flex-wrap">
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={allChecked}
                            onChange={toggleAll}
                            className="rounded"
                          />
                          <span>전체 선택</span>
                        </label>
                        {selCount > 0 && (
                          <>
                            <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded-full tabular-nums">
                              {selCount} 선택됨
                            </span>
                            <select
                              className="border border-slate-300 rounded px-1.5 py-0.5 bg-white text-slate-700"
                              defaultValue=""
                              onChange={(e) => {
                                const v = e.target.value;
                                e.target.value = "";
                                if (v) bulkReclassify(v);
                              }}
                            >
                              <option value="" disabled>
                                타입 일괄 변경…
                              </option>
                              {DOC_GROUP_ORDER.map((gk) => (
                                <optgroup
                                  key={gk}
                                  label={GROUP_META[gk]?.label || gk}
                                >
                                  {TYPE_CODES.filter(
                                    (c) => groupOf(c) === gk,
                                  ).map((c) => (
                                    <option key={c} value={c}>
                                      {prefixOf(c)} · {labelOf(c)}
                                    </option>
                                  ))}
                                </optgroup>
                              ))}
                            </select>
                            <button
                              onClick={() => setTypeSelection(new Set())}
                              className="px-2 py-0.5 text-slate-500 hover:text-slate-800"
                            >
                              해제
                            </button>
                          </>
                        )}
                        <span className="ml-auto flex items-center gap-1">
                          <span className="text-slate-400">열</span>
                          <select
                            className="border border-slate-300 rounded px-1 py-0.5 bg-white"
                            value={typeGridCols}
                            onChange={(e) =>
                              setTypeGridCols(parseInt(e.target.value, 10))
                            }
                            title="썸네일 열 개수 (auto = 창 너비 자동)"
                          >
                            <option value={0}>auto</option>
                            <option value={1}>1</option>
                            <option value={2}>2</option>
                            <option value={3}>3</option>
                            <option value={4}>4</option>
                            <option value={5}>5</option>
                            <option value={6}>6</option>
                            <option value={8}>8</option>
                            <option value={10}>10</option>
                            <option value={12}>12</option>
                          </select>
                          <span className="text-slate-400 ml-2">페이지당</span>
                          <select
                            className="border border-slate-300 rounded px-1 py-0.5 bg-white"
                            value={typePageSize}
                            onChange={(e) => {
                              setTypePageSize(parseInt(e.target.value, 10));
                              setTypePage(1);
                            }}
                          >
                            <option value={60}>60</option>
                            <option value={120}>120</option>
                            <option value={200}>200</option>
                          </select>
                          {typeLoading && (
                            <span className="text-blue-600">로딩…</span>
                          )}
                        </span>
                      </div>

                      {/* 썸네일 그리드 + 우측 분류 팔레트 */}
                      <div className="flex-1 flex min-h-0">
                        <div className="flex-1 overflow-auto p-3 min-w-0">
                          {visiblePages.length === 0 && !typeLoading && (
                            <div className="text-slate-400 text-sm p-4 text-center">
                              {typePages.length > 0 && llmOnlyDisagree
                                ? "LLM 과 일치하는 페이지만 있습니다 (불일치 없음)"
                                : "해당 조건의 페이지가 없습니다"}
                            </div>
                          )}
                          <div
                            className="grid gap-2"
                            style={{
                              gridTemplateColumns:
                                typeGridCols > 0
                                  ? `repeat(${typeGridCols}, minmax(0, 1fr))`
                                  : "repeat(auto-fill, minmax(200px, 1fr))",
                            }}
                          >
                            {visiblePages.map((p, i) => {
                              const sk = keyOf(p);
                              const selected = typeSelection.has(sk);
                              const preview =
                                typePreviewPage &&
                                typePreviewPage.id === p.id &&
                                typePreviewPage.page === p.page;
                              const llm = llmResultOf(p);
                              const llmDisagree =
                                llm && !llm.error && llm.agrees === false;
                              const llmAgree =
                                llm && !llm.error && llm.agrees === true;
                              return (
                                <div
                                  key={`${p.id}-${p.page}-${i}`}
                                  className={`relative flex flex-col bg-white border rounded overflow-hidden transition-all ${
                                    selected
                                      ? "border-slate-900 ring-1 ring-slate-200"
                                      : llmDisagree
                                        ? "border-rose-300"
                                        : llmAgree
                                          ? "border-emerald-200"
                                          : preview
                                            ? "border-slate-700"
                                            : "border-slate-200 hover:border-slate-400"
                                  }`}
                                >
                                  {/* 체크박스 — 좌상단 */}
                                  <label
                                    className="absolute top-1 left-1 z-10 bg-white/80 rounded p-0.5 cursor-pointer"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selected}
                                      onChange={() => toggleSel(sk)}
                                    />
                                  </label>
                                  {/* 신뢰도 배지 — 우상단 */}
                                  <div
                                    className={`absolute top-1 right-1 z-10 px-1.5 py-0.5 rounded text-[10px] font-mono tabular-nums bg-white/90 ${
                                      (p.confidence ?? 0) >= 0.8
                                        ? "text-emerald-700"
                                        : (p.confidence ?? 0) >= 0.5
                                          ? "text-amber-700"
                                          : "text-rose-700"
                                    }`}
                                  >
                                    {parseFloat(p.confidence || 0).toFixed(2)}
                                  </div>

                                  {/* 이미지 — 클릭 시 프리뷰 토글 (샘플로 이동하지 않음) */}
                                  <div
                                    className="aspect-[3/4] bg-slate-50 flex items-center justify-center overflow-hidden cursor-zoom-in"
                                    onClick={() =>
                                      setTypePreviewPage(
                                        preview
                                          ? null
                                          : {
                                              id: p.id,
                                              page: p.page,
                                              url: p.url,
                                              file_name: p.file_name,
                                            },
                                      )
                                    }
                                    title="클릭: 크게 보기"
                                  >
                                    {p.url ? (
                                      <img
                                        src={p.url}
                                        alt={`p${p.page}`}
                                        className="w-full h-full object-contain"
                                        loading="lazy"
                                      />
                                    ) : (
                                      <span className="text-slate-300 text-xs">
                                        no image
                                      </span>
                                    )}
                                  </div>

                                  {/* 메타 */}
                                  <div className="px-2 py-1 text-[10px] text-slate-600 border-t border-slate-100 flex items-center gap-1">
                                    <span className="font-mono text-slate-400 flex-shrink-0">
                                      #{p.id}
                                    </span>
                                    <span className="font-mono text-slate-700">
                                      p{p.page}
                                    </span>
                                    <span
                                      className="ml-auto text-slate-400 truncate"
                                      title={p.file_name}
                                    >
                                      {p.file_name}
                                    </span>
                                  </div>

                                  {/* LLM 검증 결과 오버레이 */}
                                  {llm &&
                                    (llm.error ? (
                                      <div className="px-2 py-1 text-[10px] bg-amber-50 text-amber-800 border-t border-amber-200 flex items-center gap-1">
                                        ⚠ LLM 오류:{" "}
                                        <span className="truncate">
                                          {llm.error}
                                        </span>
                                      </div>
                                    ) : llmDisagree ? (
                                      <div
                                        className="px-2 py-1 text-[10px] border-t border-rose-100 bg-rose-50/40 flex items-center gap-1"
                                        title={llm.llm_reason || ""}
                                      >
                                        {llm.tier === "opus" ||
                                        llm.tier === "opus-adviser" ? (
                                          <span
                                            className="text-[9px] text-slate-500 flex-shrink-0"
                                            title={`Opus 판정 · flash=${llm.flash_type || "-"}`}
                                          >
                                            opus
                                          </span>
                                        ) : llm.tier === "sonnet" ||
                                          llm.tier === "sonnet-adviser" ? (
                                          <span
                                            className="text-[9px] text-slate-500 flex-shrink-0"
                                            title={`Sonnet 판정 · flash=${llm.flash_type || "-"}`}
                                          >
                                            sonnet
                                          </span>
                                        ) : llm.tier === "hybrid-adviser" ||
                                          llm.tier ===
                                            "hybrid-sonnet-fallback" ? (
                                          <span
                                            className="text-[9px] text-slate-500 flex-shrink-0"
                                            title={`Hybrid(Flash→Pro→Sonnet) 판정 · flash=${llm.flash_type || "-"} · pro=${llm.pro_type || "-"}`}
                                          >
                                            hybrid
                                          </span>
                                        ) : llm.tier === "hybrid-pro" ||
                                          llm.tier === "pro-fallback" ? (
                                          <span
                                            className="text-[9px] text-slate-500 flex-shrink-0"
                                            title={`Hybrid Pro 합의 · flash=${llm.flash_type || "-"} · pro=${llm.pro_type || "-"}`}
                                          >
                                            pro
                                          </span>
                                        ) : llm.tier === "pro-adviser" ? (
                                          <span
                                            className="text-[9px] text-slate-500 flex-shrink-0"
                                            title={`Pro Adviser 판정 · flash=${llm.flash_type || "-"}`}
                                          >
                                            adv
                                          </span>
                                        ) : null}
                                        <span className="text-rose-700 font-medium flex-shrink-0">
                                          {prefixOf(llm.llm_type)}
                                        </span>
                                        <span className="text-rose-600 truncate">
                                          {labelOf(llm.llm_type)}
                                        </span>
                                        <span className="text-slate-400 tabular-nums flex-shrink-0">
                                          {(llm.llm_confidence * 100).toFixed(
                                            0,
                                          )}
                                          %
                                        </span>
                                        <button
                                          className="ml-auto px-2 py-0.5 bg-slate-900 text-white rounded-full text-[10px] hover:bg-slate-700 flex-shrink-0"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            updatePageDocType(
                                              p.id,
                                              p.page,
                                              llm.llm_type,
                                            );
                                          }}
                                          title={`이 페이지를 ${llm.llm_type} 로 변경`}
                                        >
                                          적용
                                        </button>
                                      </div>
                                    ) : (
                                      <div className="px-2 py-1 text-[10px] border-t border-slate-100 text-emerald-700 flex items-center gap-1">
                                        {llm.tier === "opus" ||
                                        llm.tier === "opus-adviser" ? (
                                          <span
                                            className="text-[9px] text-slate-500"
                                            title="Opus 판정"
                                          >
                                            opus
                                          </span>
                                        ) : llm.tier === "sonnet" ||
                                          llm.tier === "sonnet-adviser" ? (
                                          <span
                                            className="text-[9px] text-slate-500"
                                            title="Sonnet 판정"
                                          >
                                            sonnet
                                          </span>
                                        ) : llm.tier === "hybrid-adviser" ||
                                          llm.tier ===
                                            "hybrid-sonnet-fallback" ? (
                                          <span
                                            className="text-[9px] text-slate-500"
                                            title={`Hybrid(Flash→Pro→Sonnet) · pro=${llm.pro_type || "-"}`}
                                          >
                                            hybrid
                                          </span>
                                        ) : llm.tier === "hybrid-pro" ||
                                          llm.tier === "pro-fallback" ? (
                                          <span
                                            className="text-[9px] text-slate-500"
                                            title={`Flash+Pro 합의 · pro=${llm.pro_type || "-"}`}
                                          >
                                            pro
                                          </span>
                                        ) : llm.tier === "pro-adviser" ? (
                                          <span
                                            className="text-[9px] text-slate-500"
                                            title="Pro Adviser 판정"
                                          >
                                            adv
                                          </span>
                                        ) : null}
                                        <span>일치</span>
                                        <span className="text-slate-400 tabular-nums ml-auto">
                                          {(llm.llm_confidence * 100).toFixed(
                                            0,
                                          )}
                                          %
                                        </span>
                                      </div>
                                    ))}

                                  {/* 액션 — 타입 변경 / 샘플 열기 */}
                                  <div className="px-1.5 py-1 border-t border-slate-100 bg-slate-50/50 flex items-center gap-1">
                                    <select
                                      className="flex-1 min-w-0 border border-slate-300 rounded px-1 py-0.5 text-[10px] bg-white"
                                      value={p.doc_type || selectedDocType}
                                      onChange={(e) => {
                                        const nt = e.target.value;
                                        if (nt && nt !== p.doc_type)
                                          updatePageDocType(p.id, p.page, nt);
                                      }}
                                      title="이 페이지의 타입 변경"
                                    >
                                      {DOC_GROUP_ORDER.map((gk) => (
                                        <optgroup
                                          key={gk}
                                          label={GROUP_META[gk]?.label || gk}
                                        >
                                          {TYPE_CODES.filter(
                                            (c) => groupOf(c) === gk,
                                          ).map((c) => (
                                            <option key={c} value={c}>
                                              {prefixOf(c)} · {labelOf(c)}
                                            </option>
                                          ))}
                                        </optgroup>
                                      ))}
                                    </select>
                                    <button
                                      className="px-1.5 py-0.5 text-[10px] border border-slate-300 rounded bg-white hover:bg-slate-100 flex-shrink-0"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setLeftPanelMode("sample");
                                        loadDetail(p.id);
                                      }}
                                      title="이 페이지가 속한 샘플 열기"
                                    >
                                      건 열기
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* 우측 분류 팔레트 — 선택된 페이지에 원클릭으로 타입 할당 */}
                        {palettePinned ? (
                          <div className="flex-shrink-0 w-56 border-l border-slate-200 bg-white flex flex-col text-[11px]">
                            <div className="px-3 py-1.5 border-b border-slate-200 flex items-center gap-1">
                              <span className="text-[10px] uppercase tracking-wider text-slate-400">
                                분류 팔레트
                              </span>
                              <button
                                className="ml-auto px-1 py-0.5 text-slate-400 hover:text-slate-700 text-[10px]"
                                onClick={() => setPalettePinned(false)}
                                title="팔레트 접기"
                              >
                                ✕
                              </button>
                            </div>
                            <div className="px-3 py-1.5 border-b border-slate-100 flex items-center gap-1">
                              {selCount > 0 ? (
                                <>
                                  <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded-full tabular-nums">
                                    {selCount} 선택됨
                                  </span>
                                  <button
                                    className="ml-auto px-1.5 py-0.5 text-slate-500 hover:text-slate-800"
                                    onClick={() => setTypeSelection(new Set())}
                                    title="선택 해제"
                                  >
                                    해제
                                  </button>
                                </>
                              ) : (
                                <span className="text-slate-400">
                                  페이지를 선택하세요
                                </span>
                              )}
                            </div>
                            <div className="flex-1 overflow-y-auto">
                              {DOC_GROUP_ORDER.map((gk) => {
                                const codes = TYPE_CODES.filter(
                                  (c) => groupOf(c) === gk,
                                );
                                if (codes.length === 0) return null;
                                return (
                                  <div key={gk} className="mb-0.5">
                                    <div
                                      className="px-3 py-1 text-[10px] text-slate-500 bg-slate-50/60 border-y border-slate-100 truncate"
                                      title={GROUP_META[gk]?.label || gk}
                                    >
                                      {GROUP_META[gk]?.label || gk}
                                    </div>
                                    {codes.map((code) => {
                                      const isCurrent =
                                        code === selectedDocType;
                                      const disabled =
                                        selCount === 0 || isCurrent;
                                      return (
                                        <button
                                          key={code}
                                          disabled={disabled}
                                          onClick={() => bulkReclassify(code)}
                                          title={
                                            disabled
                                              ? isCurrent
                                                ? `현재 타입(${labelOf(code)}) — 변경 대상 아님`
                                                : "페이지를 먼저 선택하세요"
                                              : `선택된 ${selCount}개 페이지를 "${prefixOf(code)} · ${labelOf(code)}" 로 변경`
                                          }
                                          className={`w-full px-3 py-1 text-left flex items-center gap-1.5 border-l-2 transition-colors ${
                                            isCurrent
                                              ? "bg-slate-50 border-slate-400 text-slate-700 cursor-not-allowed"
                                              : disabled
                                                ? "border-transparent text-slate-400 cursor-not-allowed"
                                                : "border-transparent text-slate-700 hover:bg-slate-50 hover:border-slate-400 cursor-pointer"
                                          }`}
                                        >
                                          <span className="font-mono text-[10px] text-slate-500 w-16 flex-shrink-0 truncate">
                                            {prefixOf(code)}
                                          </span>
                                          <span className="truncate flex-1 min-w-0">
                                            {labelOf(code)}
                                          </span>
                                          {isCurrent && (
                                            <span className="text-[10px] text-slate-400 flex-shrink-0">
                                              현재
                                            </span>
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          <button
                            className="flex-shrink-0 w-6 border-l border-slate-200 bg-white hover:bg-slate-50 text-slate-400 hover:text-slate-700 text-[10px] flex items-center justify-center"
                            onClick={() => setPalettePinned(true)}
                            title="분류 팔레트 펼치기"
                          >
                            <span
                              style={{
                                writingMode: "vertical-rl",
                                transform: "rotate(180deg)",
                              }}
                              className="tracking-wider uppercase"
                            >
                              분류 팔레트
                            </span>
                          </button>
                        )}
                      </div>

                      {/* 페이지네이션 바 — sticky bottom */}
                      <div className="border-t border-slate-200 bg-white px-3 py-2 flex items-center gap-2 text-[11px] text-slate-600">
                        <button
                          onClick={() => setTypePage(1)}
                          disabled={typePage <= 1}
                          className="px-2 py-0.5 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40"
                        >
                          |&lt;
                        </button>
                        <button
                          onClick={() => setTypePage((p) => Math.max(1, p - 1))}
                          disabled={typePage <= 1}
                          className="px-2 py-0.5 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40"
                        >
                          &lt;
                        </button>
                        <span className="tabular-nums">
                          <input
                            type="number"
                            min={1}
                            max={totalTypePages}
                            value={typePage}
                            onChange={(e) => {
                              const v = parseInt(e.target.value, 10);
                              if (!Number.isNaN(v))
                                setTypePage(
                                  Math.min(totalTypePages, Math.max(1, v)),
                                );
                            }}
                            className="w-14 border border-slate-300 rounded px-1 py-0.5 text-center"
                          />
                          <span className="mx-1 text-slate-400">/</span>
                          <span>{totalTypePages.toLocaleString()}</span>
                        </span>
                        <button
                          onClick={() =>
                            setTypePage((p) => Math.min(totalTypePages, p + 1))
                          }
                          disabled={typePage >= totalTypePages}
                          className="px-2 py-0.5 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40"
                        >
                          &gt;
                        </button>
                        <button
                          onClick={() => setTypePage(totalTypePages)}
                          disabled={typePage >= totalTypePages}
                          className="px-2 py-0.5 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40"
                        >
                          &gt;|
                        </button>
                        <span className="ml-auto text-slate-400">
                          Shift/Space: 전체선택 · 클릭: 크게 · 건 열기: 상세
                        </span>
                      </div>

                      {/* 크게 보기 모달 (클릭한 썸네일) */}
                      {typePreviewPage && (
                        <div
                          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6"
                          onClick={() => setTypePreviewPage(null)}
                        >
                          <div
                            className="bg-white rounded shadow-lg max-w-[90vw] max-h-[90vh] flex flex-col overflow-hidden"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="px-3 py-2 border-b border-slate-200 flex items-center gap-3 text-xs">
                              <span className="font-mono text-slate-500">
                                #{typePreviewPage.id}
                              </span>
                              <span className="font-mono text-slate-700">
                                p{typePreviewPage.page}
                              </span>
                              <span className="truncate text-slate-600">
                                {typePreviewPage.file_name}
                              </span>
                              <button
                                className="ml-auto px-2 py-0.5 border border-slate-300 rounded hover:bg-slate-100"
                                onClick={() => setTypePreviewPage(null)}
                              >
                                닫기
                              </button>
                            </div>
                            <div className="flex-1 overflow-auto bg-slate-50 flex items-center justify-center p-4">
                              {typePreviewPage.url ? (
                                <img
                                  src={typePreviewPage.url}
                                  alt={`p${typePreviewPage.page}`}
                                  className="max-w-full max-h-[80vh] object-contain"
                                />
                              ) : (
                                <span className="text-slate-400">no image</span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
            </div>
          )}

          {/* ═══ 불일치 그룹 뷰 ═══ */}
          {leftPanelMode === "mismatchGroups" && (
            <div className="flex-1 flex flex-col min-h-0">
              {!selectedDocType && (
                <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
                  왼쪽에서 문서 타입을 선택하세요 — LLM 제안 타입별로 묶어서
                  보여줍니다
                </div>
              )}
              {selectedDocType && (
                <>
                  {/* 헤더 */}
                  <div className="px-4 py-2.5 bg-white border-b border-slate-200 flex items-center gap-2 flex-wrap">
                    <span className="text-[12px] font-semibold text-slate-800">
                      🔀 {selectedDocType} · LLM 불일치 그룹
                    </span>
                    {mismatchGroupsData && (
                      <span className="text-[11px] text-slate-500">
                        총{" "}
                        <span className="text-rose-600 font-semibold tabular-nums">
                          {(
                            mismatchGroupsData.total_mismatch || 0
                          ).toLocaleString()}
                        </span>
                        건이
                        <span className="font-semibold text-slate-700">
                          {" "}
                          {mismatchGroupsData.groups?.length || 0}개
                        </span>{" "}
                        타입으로 흩어짐
                      </span>
                    )}
                    <button
                      onClick={async () => {
                        if (!selectedDocType) return;
                        // harness-adviser 강제 + suspectOnly 로 재합의
                        await runLlmReclassifyAllInType({
                          type: selectedDocType,
                          suspectOnly: true,
                          model: "harness-adviser",
                        });
                        // 재검증 후 그룹 갱신
                        await fetchMismatchGroups(selectedDocType);
                        await fetchTypeCounts();
                      }}
                      disabled={
                        llmRunning || mismatchBulkRunning || !selectedDocType
                      }
                      className="ml-auto text-[11px] px-2 py-0.5 border border-purple-300 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded disabled:opacity-40"
                      title="현재 타입의 모든 LLM 불일치 페이지를 harness-adviser (Flash topK → Pro verifier × top-2 → Sonnet tie-break) 로 강제 재합의. 합의된 새 타입은 llm_verify 에 갱신됩니다."
                    >
                      🛡 harness 강제 재합의
                    </button>
                    {llmRunning && (
                      <span className="text-[11px] text-purple-600 tabular-nums">
                        ⏳ 재합의 {llmProgress.done.toLocaleString()} /{" "}
                        {llmProgress.total.toLocaleString()}
                      </span>
                    )}
                    <button
                      onClick={() => fetchMismatchGroups(selectedDocType)}
                      disabled={mismatchGroupsLoading || mismatchBulkRunning}
                      className="text-[11px] px-2 py-0.5 border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-40"
                      title="그룹 다시 불러오기"
                    >
                      🔄 새로고침
                    </button>
                    {mismatchBulkRunning && (
                      <span className="text-[11px] text-blue-600 tabular-nums">
                        ⏳ {mismatchBulkProgress.label}:{" "}
                        {mismatchBulkProgress.done.toLocaleString()} /{" "}
                        {mismatchBulkProgress.total.toLocaleString()}
                      </span>
                    )}
                  </div>

                  {/* 본문 */}
                  <div className="flex-1 overflow-y-auto p-3 bg-slate-50">
                    {mismatchGroupsLoading && (
                      <div className="flex items-center justify-center py-12 text-slate-400 text-sm">
                        불일치 그룹 집계 중...
                      </div>
                    )}
                    {!mismatchGroupsLoading &&
                      mismatchGroupsData &&
                      mismatchGroupsData.groups?.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-12 text-slate-400 text-sm gap-2">
                          <div>이 타입에는 LLM 불일치 페이지가 없습니다.</div>
                          <div className="text-[11px]">
                            먼저 「타입별 뷰」에서 LLM 재검증을 실행하세요.
                          </div>
                        </div>
                      )}
                    {!mismatchGroupsLoading &&
                      mismatchGroupsData &&
                      mismatchGroupsData.groups?.map((group) => {
                        const groupKey = group.llm_type;
                        const expanded = mismatchExpandedGroup === groupKey;
                        const llmTypeLabel = labelOf(groupKey);
                        const llmTypePrefix = prefixOf(groupKey);
                        const groupDocGroupKey = groupOf(groupKey);
                        return (
                          <div
                            key={groupKey}
                            className="mb-3 bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm"
                          >
                            {/* 그룹 헤더 */}
                            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-2 flex-wrap">
                              <button
                                onClick={() =>
                                  setMismatchExpandedGroup(
                                    expanded ? null : groupKey,
                                  )
                                }
                                className="flex items-center gap-1.5 text-[12px] font-semibold text-slate-800 hover:text-blue-700"
                                title={expanded ? "접기" : "미리보기 펼치기"}
                              >
                                <span className="text-slate-400">
                                  {expanded ? "▾" : "▸"}
                                </span>
                                <span className="font-mono text-[10px] text-slate-500 px-1.5 py-0.5 bg-slate-100 rounded">
                                  {llmTypePrefix}
                                </span>
                                <span>{llmTypeLabel}</span>
                              </button>
                              <span className="text-[11px] text-slate-400">
                                (
                                {GROUP_META[groupDocGroupKey]?.label ||
                                  groupDocGroupKey}
                                )
                              </span>
                              <span className="ml-2 text-[11px] tabular-nums text-rose-600 font-semibold">
                                {Number(group.n).toLocaleString()}건
                              </span>

                              <div className="ml-auto flex items-center gap-1">
                                {/* 그룹 액션 */}
                                <button
                                  onClick={() =>
                                    applyMismatchGroupBulk(
                                      group,
                                      groupKey,
                                      "all",
                                    )
                                  }
                                  disabled={mismatchBulkRunning}
                                  className="text-[11px] px-2 py-0.5 border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded disabled:opacity-40"
                                  title={`이 그룹의 ${Number(group.n).toLocaleString()}건 전체를 [${groupKey}] 으로 적용`}
                                >
                                  ✓ 전체 → {groupKey}
                                </button>
                                <button
                                  onClick={() =>
                                    applyMismatchGroupBulk(
                                      group,
                                      "OTHER",
                                      "all",
                                    )
                                  }
                                  disabled={
                                    mismatchBulkRunning || groupKey === "OTHER"
                                  }
                                  className="text-[11px] px-2 py-0.5 border border-stone-300 bg-stone-50 hover:bg-stone-100 text-stone-700 rounded disabled:opacity-40"
                                  title="이 그룹 전체를 OTHER (분류불가) 로 흡수"
                                >
                                  → OTHER
                                </button>
                                <select
                                  defaultValue=""
                                  onChange={(e) => {
                                    const newType = e.target.value;
                                    e.target.value = "";
                                    if (!newType) return;
                                    applyMismatchGroupBulk(
                                      group,
                                      newType,
                                      "all",
                                    );
                                  }}
                                  disabled={mismatchBulkRunning}
                                  className="text-[11px] px-1 py-0.5 border border-slate-300 rounded bg-white text-slate-700 disabled:opacity-40"
                                  title="다른 타입으로 일괄 변경"
                                >
                                  <option value="">→ 다른 타입...</option>
                                  {DOC_GROUP_ORDER.map((gk) => {
                                    const codes = TYPE_CODES.filter(
                                      (c) =>
                                        groupOf(c) === gk && c !== groupKey,
                                    );
                                    if (codes.length === 0) return null;
                                    return (
                                      <optgroup
                                        key={gk}
                                        label={GROUP_META[gk]?.label || gk}
                                      >
                                        {codes.map((c) => (
                                          <option key={c} value={c}>
                                            {c} · {labelOf(c)}
                                          </option>
                                        ))}
                                      </optgroup>
                                    );
                                  })}
                                </select>
                              </div>
                            </div>

                            {/* 미리보기 썸네일 */}
                            {expanded && (
                              <div className="p-2">
                                {(group.sample_pages?.length || 0) === 0 && (
                                  <div className="text-[11px] text-slate-400 px-2 py-3">
                                    미리보기 없음
                                  </div>
                                )}
                                {group.sample_pages?.length > 0 && (
                                  <div
                                    className="grid gap-2"
                                    style={{
                                      gridTemplateColumns:
                                        "repeat(auto-fill, minmax(140px, 1fr))",
                                    }}
                                  >
                                    {group.sample_pages.map((p) => {
                                      const llmConf =
                                        p.llm_confidence != null
                                          ? Number(p.llm_confidence).toFixed(2)
                                          : "?";
                                      const origConf =
                                        p.confidence != null
                                          ? Number(p.confidence).toFixed(2)
                                          : "?";
                                      return (
                                        <div
                                          key={`${p.id}:${p.page}`}
                                          className="border border-slate-200 rounded overflow-hidden bg-white relative group"
                                        >
                                          {p.url ? (
                                            <img
                                              src={p.url}
                                              alt={`${p.id}:${p.page}`}
                                              className="w-full h-32 object-cover"
                                              loading="lazy"
                                            />
                                          ) : (
                                            <div className="w-full h-32 bg-slate-50 flex items-center justify-center text-slate-300 text-xs">
                                              no image
                                            </div>
                                          )}
                                          <div
                                            className="absolute top-1 right-1 text-[9px] bg-rose-600/85 text-white px-1 rounded tabular-nums"
                                            title="LLM 신뢰도"
                                          >
                                            LLM {llmConf}
                                          </div>
                                          <div
                                            className="absolute top-1 left-1 text-[9px] bg-slate-700/80 text-white px-1 rounded tabular-nums"
                                            title="원본 분류기 신뢰도"
                                          >
                                            {origConf}
                                          </div>
                                          <div className="px-1.5 py-1 text-[10px] text-slate-600 border-t border-slate-100 flex items-center gap-1">
                                            <span className="font-mono text-slate-400">
                                              #{p.id} p{p.page}
                                            </span>
                                            <button
                                              className="ml-auto text-[10px] text-blue-600 hover:underline"
                                              onClick={() =>
                                                setMismatchPreviewPage({
                                                  ...p,
                                                  group_from:
                                                    mismatchGroupsData?.type,
                                                  group_suggested: groupKey,
                                                })
                                              }
                                              title="우측 패널에서 크게 보기"
                                            >
                                              크게 보기
                                            </button>
                                          </div>
                                          {p.llm_reason && (
                                            <div
                                              className="px-1.5 pb-1 text-[9px] text-slate-500 line-clamp-2"
                                              title={p.llm_reason}
                                            >
                                              {p.llm_reason}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                                <div className="mt-2 text-[10px] text-slate-400 px-1">
                                  ↑ 신뢰도가 낮은 순 미리보기{" "}
                                  {group.sample_pages?.length || 0}건. 일괄
                                  적용은 그룹 전체{" "}
                                  {Number(group.n).toLocaleString()}건 대상.
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </>
              )}
            </div>
          )}

          {leftPanelMode === "sample" && !selectedId && (
            <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
              왼쪽에서 샘플을 선택하세요
            </div>
          )}
          {leftPanelMode === "sample" && selectedId && !imageUrl && (
            <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
              이미지 로딩 중...
            </div>
          )}
          {leftPanelMode === "sample" && imageUrl && (
            <>
              <div className="px-4 py-2 bg-white border-b border-gray-200 text-xs text-gray-600 flex items-center gap-3 flex-wrap">
                <span>📄 {selectedDetail?.file_name}</span>
                {selectedDetail && qualityBadge(selectedDetail.quality_label)}
                <span className="text-gray-400">
                  conf=
                  {parseFloat(selectedDetail?.confidence_score || 0).toFixed(2)}
                </span>
                {selectedDetail?.verified_at && (
                  <span className="text-emerald-600">
                    ✓ 검증됨:{" "}
                    {new Date(selectedDetail.verified_at).toLocaleString()}
                  </span>
                )}
                {pagesList.length > 1 && (
                  <span className="ml-auto flex items-center gap-1.5 text-slate-600">
                    <button
                      className="px-2 py-0.5 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40"
                      disabled={currentPageIdx === 0}
                      onClick={() => {
                        const i = Math.max(0, currentPageIdx - 1);
                        setCurrentPageIdx(i);
                        if (pagesList[i]?.url) setImageUrl(pagesList[i].url);
                      }}
                    >
                      ◀
                    </button>
                    <span className="tabular-nums">
                      {currentPageIdx + 1} / {pagesList.length}
                    </span>
                    <button
                      className="px-2 py-0.5 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40"
                      disabled={currentPageIdx >= pagesList.length - 1}
                      onClick={() => {
                        const i = Math.min(
                          pagesList.length - 1,
                          currentPageIdx + 1,
                        );
                        setCurrentPageIdx(i);
                        if (pagesList[i]?.url) setImageUrl(pagesList[i].url);
                      }}
                    >
                      ▶
                    </button>
                    {pagesList[currentPageIdx]?.doc_type &&
                      (() => {
                        const curPage = pagesList[currentPageIdx];
                        return (
                          <select
                            value={curPage.doc_type}
                            onChange={async (e) => {
                              const nt = e.target.value;
                              if (!nt || nt === curPage.doc_type) return;
                              if (
                                !window.confirm(
                                  `page ${curPage.page} 타입을 ${curPage.doc_type} → ${nt} 로 변경하시겠습니까?`,
                                )
                              )
                                return;
                              await updatePageDocType(
                                selectedId,
                                curPage.page,
                                nt,
                              );
                            }}
                            title={`현재 타입: ${curPage.doc_type} · 변경하려면 선택`}
                            className="ml-1 px-1.5 py-0.5 bg-slate-100 text-slate-700 text-[10px] font-mono rounded border border-slate-200 hover:bg-white cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            {DOC_GROUP_ORDER.map((gk) => (
                              <optgroup
                                key={gk}
                                label={GROUP_META[gk]?.label || gk}
                              >
                                {TYPE_CODES.filter(
                                  (c) => groupOf(c) === gk,
                                ).map((c) => (
                                  <option key={c} value={c}>
                                    {prefixOf(c)} · {labelOf(c)}
                                  </option>
                                ))}
                              </optgroup>
                            ))}
                          </select>
                        );
                      })()}
                  </span>
                )}
              </div>
              <div className="flex-1 flex min-h-0">
                {pagesList.length > 1 &&
                  (() => {
                    // 페이지를 doc_type 기준으로 그룹핑하되 평평한 페이지 리스트로 렌더.
                    const { source, result } = groupPagesForViewer(pagesList);
                    // 이 샘플에 등장하는 doc_type 목록 (설명 팝업용)
                    const allGroups = [...source, ...result]
                      .flatMap((s) => s.entries)
                      .map((e) => e.group);
                    const uniqueTypes = Array.from(
                      new Map(allGroups.map((g) => [g.key, g])).values(),
                    );
                    // 평평한 리스트 생성 — 그룹 순서 유지, 같은 타입 페이지는 붙어있게
                    const flatItems = [...source, ...result].flatMap((s) =>
                      s.entries.flatMap((e) => e.items),
                    );
                    return (
                      <div className="flex-shrink-0 w-44 bg-white border-r border-slate-200 overflow-auto text-[11px]">
                        <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-slate-200 bg-slate-50">
                          <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">
                            페이지
                          </span>
                          <button
                            className="w-4 h-4 inline-flex items-center justify-center text-[9px] text-slate-500 border border-slate-300 rounded-full hover:bg-white hover:text-slate-700"
                            onClick={() => setShowDocTypeHelp(true)}
                            title="이 샘플의 문서 타입 설명 보기"
                          >
                            ?
                          </button>
                        </div>
                        {flatItems.map((it) => {
                          const globalIdx = pagesList.findIndex(
                            (p) => p.page === it.page,
                          );
                          const active = globalIdx === currentPageIdx;
                          return (
                            <button
                              key={it.page}
                              onClick={() => {
                                setCurrentPageIdx(globalIdx);
                                if (it.url) setImageUrl(it.url);
                              }}
                              title={`page ${it.page} · ${it.doc_type} · conf ${it.confidence ?? "?"}`}
                              className={`w-full px-2.5 py-1 text-left text-[11px] flex items-center gap-1.5 font-mono tabular-nums border-l-2 ${active ? "bg-slate-800 text-white border-slate-800" : "border-transparent text-slate-700 hover:bg-slate-100"}`}
                            >
                              <span
                                className={
                                  active ? "text-slate-300" : "text-slate-400"
                                }
                              >
                                {String(it.page).padStart(2, " ")}
                              </span>
                              <span className="truncate">{it.label}</span>
                            </button>
                          );
                        })}
                        {showDocTypeHelp && (
                          <div
                            className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
                            onClick={() => setShowDocTypeHelp(false)}
                          >
                            <div
                              className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-auto"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200">
                                <div className="text-sm font-semibold text-slate-800">
                                  이 샘플의 문서 타입
                                </div>
                                <button
                                  className="text-slate-400 hover:text-slate-700 text-lg leading-none"
                                  onClick={() => setShowDocTypeHelp(false)}
                                >
                                  ×
                                </button>
                              </div>
                              <div className="p-3 text-[12px]">
                                {uniqueTypes.map((g) => (
                                  <div
                                    key={g.key}
                                    className="flex gap-2 py-1.5 border-b border-slate-100 last:border-0"
                                  >
                                    <span className="font-mono text-slate-500 w-14 flex-shrink-0">
                                      {g.prefix}
                                    </span>
                                    <span className="text-slate-700">
                                      {g.label}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                <div className="flex-1 overflow-auto p-4">
                  <img
                    src={imageUrl}
                    alt={selectedDetail?.file_name || ""}
                    className="max-w-full mx-auto shadow-lg"
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {/* ═══ RIGHT: 편집 폼 + 왼쪽 리사이저 ═══ */}
        <div
          className="flex-shrink-0 border-l border-gray-200 bg-white flex flex-col relative"
          style={{ width: rightPanelWidth }}
        >
          {/* 리사이즈 핸들 (왼쪽 가장자리) — AIChatPanel과 동일 스타일 */}
          <div
            className="absolute left-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-blue-100 transition-colors flex items-center justify-center group z-20"
            onMouseDown={(e) => {
              e.preventDefault();
              setIsResizing(true);
            }}
            title={`패널 폭 조정 (현재 ${rightPanelWidth}px)`}
          >
            <div
              className={`w-1 h-16 rounded-full transition-colors ${isResizing ? "bg-blue-500" : "bg-gray-300 group-hover:bg-gray-500"}`}
            />
          </div>
          {leftPanelMode === "mismatchGroups" &&
            mismatchPreviewPage &&
            (() => {
              const p = mismatchPreviewPage;
              const origType = p.group_from || p.doc_type;
              const suggested = p.group_suggested || p.llm_type;
              const llmConfPct =
                p.llm_confidence != null
                  ? `${(Number(p.llm_confidence) * 100).toFixed(0)}%`
                  : "?";
              const origConfPct =
                p.confidence != null
                  ? `${(Number(p.confidence) * 100).toFixed(0)}%`
                  : "?";
              return (
                <div className="flex-1 flex flex-col min-h-0 min-w-0">
                  <div className="px-4 py-2 border-b border-slate-200 bg-white flex items-center gap-2 flex-wrap">
                    <span className="text-[12px] font-semibold text-slate-800">
                      뷰어 미리보기
                    </span>
                    <span className="text-[11px] text-slate-400 font-mono">
                      #{p.id} p{p.page}
                    </span>
                    {p.file_name && (
                      <span
                        className="text-[11px] text-slate-500 truncate max-w-[240px]"
                        title={p.file_name}
                      >
                        {p.file_name}
                      </span>
                    )}
                    <button
                      className="ml-auto text-[11px] px-2 py-0.5 border border-slate-300 rounded hover:bg-slate-50 text-slate-600"
                      onClick={() => {
                        setLeftPanelMode("sample");
                        loadDetail(p.id);
                        setMismatchPreviewPage(null);
                      }}
                      title="샘플 상세(편집 폼) 로 이동"
                    >
                      샘플 열기 ↗
                    </button>
                    <button
                      className="text-[11px] px-1.5 py-0.5 text-slate-400 hover:text-slate-800"
                      onClick={() => setMismatchPreviewPage(null)}
                      title="미리보기 닫기"
                    >
                      ✕
                    </button>
                  </div>

                  <div className="px-4 py-2 border-b border-slate-100 bg-slate-50/60 text-[11px] text-slate-600 flex items-center gap-3 flex-wrap">
                    <span className="flex items-center gap-1">
                      <span className="text-slate-400">현재</span>
                      <span className="font-mono text-[10px] px-1.5 py-0.5 bg-white border border-slate-200 rounded">
                        {prefixOf(origType)}
                      </span>
                      <span>{labelOf(origType)}</span>
                      <span className="text-slate-400 tabular-nums">
                        · {origConfPct}
                      </span>
                    </span>
                    <span className="text-slate-300">→</span>
                    <span className="flex items-center gap-1">
                      <span className="text-rose-500">LLM 제안</span>
                      <span className="font-mono text-[10px] px-1.5 py-0.5 bg-rose-50 text-rose-700 border border-rose-200 rounded">
                        {prefixOf(suggested)}
                      </span>
                      <span className="text-rose-700">
                        {labelOf(suggested)}
                      </span>
                      <span className="text-slate-400 tabular-nums">
                        · {llmConfPct}
                      </span>
                    </span>
                    <button
                      className="ml-auto px-2 py-0.5 bg-slate-900 text-white rounded-full hover:bg-slate-700 text-[11px]"
                      onClick={() => updatePageDocType(p.id, p.page, suggested)}
                      title={`이 페이지만 ${suggested} 로 변경`}
                    >
                      이 페이지 → {suggested}
                    </button>
                  </div>

                  {p.llm_reason && (
                    <div
                      className="px-4 py-1.5 text-[11px] text-slate-500 border-b border-slate-100 bg-white"
                      title={p.llm_reason}
                    >
                      <span className="text-slate-400">이유: </span>
                      {p.llm_reason}
                    </div>
                  )}

                  <div className="flex-1 overflow-auto p-4 bg-slate-100">
                    {p.url ? (
                      <img
                        src={p.url}
                        alt={`#${p.id} p${p.page}`}
                        className="max-w-full mx-auto shadow-lg bg-white"
                      />
                    ) : (
                      <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                        이미지 URL 없음
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          {!selectedDetail &&
            !(leftPanelMode === "mismatchGroups" && mismatchPreviewPage) && (
              <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
                {leftPanelMode === "mismatchGroups"
                  ? "불일치 그룹에서 썸네일의 「크게 보기」를 누르세요"
                  : "샘플을 선택하면 편집 폼이 표시됩니다"}
              </div>
            )}
          {selectedDetail &&
            editedData &&
            !(leftPanelMode === "mismatchGroups" && mismatchPreviewPage) && (
              <>
                <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0 flex-shrink">
                    <span className="text-sm font-semibold text-gray-800 whitespace-nowrap">
                      검증 필드
                    </span>
                    <select
                      value={selectedDetail.document_type || ""}
                      onChange={async (e) => {
                        const newType = e.target.value;
                        await fetch(
                          `/api/training-samples/${selectedId}/document-type`,
                          {
                            method: "PUT",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ document_type: newType }),
                          },
                        ).catch(() => {});
                        setSelectedDetail({
                          ...selectedDetail,
                          document_type: newType,
                        });
                        fetchList();
                      }}
                      className="text-[11px] border border-gray-300 rounded px-1.5 py-0.5 bg-white"
                      title="문서 유형 (틀리면 수정)"
                    >
                      <option value="Commercial Invoice">CI</option>
                      <option value="Bill of Lading">BL</option>
                      <option value="Packing List">PL</option>
                      <option value="Certificate of Origin">CO</option>
                      <option value="Mixed (CI+PL)">MIX</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-1">
                    {/* 뷰 모드 탭 */}
                    <div className="flex rounded border border-gray-300 overflow-hidden mr-2">
                      <button
                        onClick={() => setViewMode("form")}
                        className={`text-[11px] px-2 py-1 flex items-center gap-1 ${viewMode === "form" ? "bg-blue-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                        title="폼 뷰"
                      >
                        <List size={11} /> 폼
                      </button>
                      <button
                        onClick={() => setViewMode("grid")}
                        className={`text-[11px] px-2 py-1 flex items-center gap-1 ${viewMode === "grid" ? "bg-blue-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                        title="그리드 뷰 (AUIGrid)"
                      >
                        <LayoutGrid size={11} /> 그리드
                      </button>
                    </div>
                    <button
                      onClick={resetEdits}
                      className="text-xs text-gray-500 hover:text-gray-800 flex items-center gap-1"
                    >
                      <RotateCcw size={12} /> 되돌리기
                    </button>
                  </div>
                </div>

                {viewMode === "form" && (
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {/* Header 필드 */}
                    <div>
                      <div className="text-xs font-semibold text-gray-600 mb-2">
                        Header
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {HEADER_FIELDS.map((f) => (
                          <div key={f}>
                            <label className="text-[10px] text-gray-500">
                              {f}
                            </label>
                            <input
                              type="text"
                              value={editedData[f] ?? ""}
                              onChange={(e) =>
                                updateHeaderField(f, e.target.value)
                              }
                              className="w-full text-xs border border-gray-300 rounded px-2 py-1"
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Items 테이블 (폼) */}
                    <div>
                      <div className="text-xs font-semibold text-gray-600 mb-2 flex items-center justify-between">
                        <span>Items ({editedData.items?.length || 0})</span>
                        <button
                          onClick={addItem}
                          className="text-[10px] bg-blue-50 text-blue-700 border border-blue-200 rounded px-2 py-0.5 flex items-center gap-1 hover:bg-blue-100"
                        >
                          <Plus size={10} /> 행 추가
                        </button>
                      </div>
                      <div className="space-y-2">
                        {(editedData.items || []).map((item, idx) => (
                          <div
                            key={idx}
                            className="border border-gray-200 rounded p-2 bg-gray-50"
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[10px] text-gray-500">
                                #{idx + 1}
                              </span>
                              <button
                                onClick={() => removeItem(idx)}
                                className="text-[10px] text-red-500 hover:text-red-700 flex items-center gap-0.5"
                              >
                                <Trash2 size={10} /> 삭제
                              </button>
                            </div>
                            <div className="grid grid-cols-2 gap-1">
                              {ITEM_FIELDS.map((f) => (
                                <div key={f}>
                                  <label className="text-[9px] text-gray-500">
                                    {f}
                                  </label>
                                  <input
                                    type="text"
                                    value={item[f] ?? ""}
                                    onChange={(e) =>
                                      updateItemField(idx, f, e.target.value)
                                    }
                                    className="w-full text-[11px] border border-gray-300 rounded px-1.5 py-0.5"
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* 메타 정보 */}
                    <div className="border-t pt-3 mt-4 text-[10px] text-gray-500 space-y-1">
                      <div>source: {selectedDetail.source}</div>
                      <div>sampleId: {selectedDetail.source_sample_id}</div>
                      <div>
                        primary: {selectedDetail.primary_model} · critic:{" "}
                        {selectedDetail.critic_model}
                      </div>
                      <div>
                        training_runs: [
                        {(selectedDetail.training_runs || []).join(", ")}]
                      </div>
                    </div>
                  </div>
                )}

                {viewMode === "grid" && (
                  <div className="flex-1 flex flex-col overflow-hidden">
                    {/* Header 필드 (그리드 모드에서도 상단 compact하게) */}
                    <div className="p-3 border-b border-gray-200 bg-white">
                      <div className="text-xs font-semibold text-gray-600 mb-1.5">
                        Header
                      </div>
                      <div className="grid grid-cols-3 gap-1.5">
                        {HEADER_FIELDS.map((f) => (
                          <div key={f}>
                            <label className="text-[9px] text-gray-500">
                              {f}
                            </label>
                            <input
                              type="text"
                              value={editedData[f] ?? ""}
                              onChange={(e) =>
                                updateHeaderField(f, e.target.value)
                              }
                              className="w-full text-[11px] border border-gray-300 rounded px-1.5 py-0.5"
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Items AUIGrid */}
                    <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-600">
                        Items ({editedData.items?.length || 0})
                      </span>
                      <div className="flex gap-1">
                        <button
                          onClick={addItemGrid}
                          className="text-[10px] bg-blue-50 text-blue-700 border border-blue-200 rounded px-2 py-0.5 flex items-center gap-1 hover:bg-blue-100"
                        >
                          <Plus size={10} /> 행 추가
                        </button>
                        <button
                          onClick={removeItemGrid}
                          className="text-[10px] bg-red-50 text-red-700 border border-red-200 rounded px-2 py-0.5 flex items-center gap-1 hover:bg-red-100"
                        >
                          <Trash2 size={10} /> 선택행 삭제
                        </button>
                      </div>
                    </div>
                    <div
                      className="flex-1 overflow-hidden flex flex-col"
                      style={{ minHeight: 300 }}
                    >
                      <RichInvoiceGrid
                        gridId={gridId}
                        columns={ITEM_COLUMN_DEFS}
                        data={editedData.items || []}
                        onChange={handleGridChange}
                        onCellClick={handleCellClick}
                        height="fill"
                      />
                    </div>

                    {/* 메타 정보 */}
                    <div className="px-3 py-2 border-t bg-gray-50 text-[10px] text-gray-500 flex flex-wrap gap-x-3">
                      <span>source: {selectedDetail.source}</span>
                      <span>id: {selectedDetail.source_sample_id}</span>
                      <span>primary: {selectedDetail.primary_model}</span>
                      <span>critic: {selectedDetail.critic_model}</span>
                    </div>
                  </div>
                )}

                {/* ─── 재추출 Draft 패널 ─── */}
                {draftData && (
                  <div className="border-t-2 border-blue-400 bg-blue-50/30">
                    <div className="px-3 py-1.5 bg-blue-100 border-b border-blue-200 flex items-center justify-between">
                      <div className="flex items-center gap-2 text-[11px] text-blue-900">
                        <Sparkles size={12} className="text-blue-600" />
                        <span className="font-semibold">재추출 Draft</span>
                        <span className="text-blue-700">
                          · {draftData.templateName || "(no template)"}
                        </span>
                        <span className="text-blue-700">
                          · {draftData.model}
                        </span>
                        <span className="text-blue-600">
                          · {(draftData.elapsedMs / 1000).toFixed(1)}s
                        </span>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => {
                            if (
                              !window.confirm(
                                "draft 값으로 현재 편집 내용을 전체 덮어쓸까요?\n(저장 전까지는 되돌리기 가능)",
                              )
                            )
                              return;
                            setEditedData((prev) => {
                              const next = { ...prev };
                              HEADER_FIELDS.forEach((f) => {
                                next[f] = draftData.data[f] ?? "";
                              });
                              next.items = (draftData.data.items || []).map(
                                (r) => ({ ...r }),
                              );
                              return next;
                            });
                            setDraftApplied({
                              model: draftData.model,
                              templateName: draftData.templateName,
                              appliedHeaders: HEADER_FIELDS.slice(),
                              appliedItems: true,
                            });
                          }}
                          className="text-[10px] bg-blue-600 text-white rounded px-2 py-0.5 hover:bg-blue-700"
                          title="draft 값을 편집 영역에 전체 적용"
                        >
                          전체 적용
                        </button>
                        <button
                          onClick={() => {
                            if (!draftData.data.items) return;
                            setEditedData((prev) => ({
                              ...prev,
                              items: (draftData.data.items || []).map((r) => ({
                                ...r,
                              })),
                            }));
                            setDraftApplied((prev) => ({
                              model: draftData.model,
                              templateName: draftData.templateName,
                              appliedHeaders:
                                (prev && prev.appliedHeaders) || [],
                              appliedItems: true,
                            }));
                          }}
                          className="text-[10px] bg-white border border-blue-300 text-blue-700 rounded px-2 py-0.5 hover:bg-blue-50"
                          title="items만 draft 값으로 교체"
                        >
                          items만 적용
                        </button>
                        <button
                          onClick={() => setDraftData(null)}
                          className="text-[10px] bg-white border border-gray-300 text-gray-600 rounded px-2 py-0.5 hover:bg-gray-50"
                        >
                          닫기
                        </button>
                      </div>
                    </div>
                    <div className="p-2 max-h-[220px] overflow-y-auto text-[10.5px] font-mono text-gray-700 bg-white/70">
                      <div className="mb-1 text-[10px] text-gray-500">
                        Header (클릭해서 필드 단위로 적용)
                      </div>
                      <div className="mb-1.5 flex flex-wrap gap-1">
                        {HEADER_FIELDS.map((f) => {
                          const dv = draftData.data[f] ?? "";
                          const cv = editedData?.[f] ?? "";
                          const diff = String(dv) !== String(cv);
                          return (
                            <button
                              key={f}
                              onClick={() => {
                                if (String(dv) === String(cv)) return;
                                setEditedData((prev) => ({ ...prev, [f]: dv }));
                                setDraftApplied((prev) => {
                                  const headers = new Set(
                                    (prev && prev.appliedHeaders) || [],
                                  );
                                  headers.add(f);
                                  return {
                                    model: draftData.model,
                                    templateName: draftData.templateName,
                                    appliedHeaders: Array.from(headers),
                                    appliedItems:
                                      (prev && prev.appliedItems) || false,
                                  };
                                });
                              }}
                              disabled={!diff}
                              className={`text-left px-1.5 py-0.5 rounded border ${
                                diff
                                  ? "bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100 cursor-pointer"
                                  : "bg-gray-50 border-gray-200 text-gray-400 cursor-default"
                              }`}
                              title={
                                diff
                                  ? `클릭: 이 필드만 draft 값으로 교체\n현재: ${cv || "∅"}\ndraft: ${dv || "∅"}`
                                  : "현재와 동일"
                              }
                            >
                              {f}=
                              {String(dv).length > 28
                                ? String(dv).slice(0, 28) + "…"
                                : dv || "∅"}
                            </button>
                          );
                        })}
                      </div>
                      <div className="text-gray-600">
                        items: {draftData.data.items?.length || 0}건
                        {(editedData?.items?.length || 0) !==
                          (draftData.data.items?.length || 0) && (
                          <span className="text-rose-700">
                            {" "}
                            (현재 {editedData?.items?.length || 0}건)
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* 액션 버튼 */}
                <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex gap-2">
                  <button
                    onClick={saveAsVerified}
                    disabled={saving}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded px-3 py-2 flex items-center justify-center gap-1 disabled:opacity-50"
                  >
                    <Save size={12} />
                    {saving ? "저장 중..." : "검증 완료 (human_verified)"}
                  </button>
                  <button
                    onClick={rejectSample}
                    disabled={saving}
                    className="bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-semibold rounded px-3 py-2 flex items-center gap-1 disabled:opacity-50 border border-amber-300"
                    title="학습에서 제외"
                  >
                    <AlertTriangle size={12} />
                    보류
                  </button>
                </div>
              </>
            )}
        </div>
      </div>

      {/* 프롬프트 편집 모달 */}
      {promptEditorOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setPromptEditorOpen(false);
          }}
        >
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-800">
                  ✏️ 분류 프롬프트 편집
                </span>
                <span className="text-[11px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">
                  Pass {llmPass}
                </span>
                {customPrompt && (
                  <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                    커스텀 저장됨
                  </span>
                )}
              </div>
              <button
                className="text-slate-400 hover:text-slate-700 text-lg leading-none"
                onClick={() => setPromptEditorOpen(false)}
                title="닫기"
              >
                ×
              </button>
            </div>
            <div className="px-4 py-2 bg-slate-50 text-[11px] text-slate-600 border-b border-slate-200 space-y-1">
              <div>
                · 원하는 지시어를 자유롭게 추가/수정하세요. 예:{" "}
                <code className="bg-white px-1 rounded">
                  "BL 은 선하증권/항공운송장만. 이메일·첨부서신·내부메모는 OTHER
                  로 분류한다."
                </code>
              </div>
              <div>
                · LLM 이 반환해야 하는 <b>JSON 스키마</b>
                (type/confidence/reason)는 반드시 유지하세요. 스키마 문구가
                제거되면 분류가 깨집니다.
              </div>
              <div>
                · pass={llmPass} 프롬프트는 브라우저 localStorage 에 저장됩니다
                (pass별 분리).
              </div>
            </div>
            <div className="flex-1 min-h-0 p-3 flex flex-col gap-2 overflow-auto">
              <textarea
                className="w-full flex-1 min-h-[420px] font-mono text-[12px] border border-slate-300 rounded p-2 resize-none leading-relaxed"
                value={promptDraft}
                onChange={(e) => setPromptDraft(e.target.value)}
                spellCheck={false}
                placeholder={defaultPrompt ? "" : "불러오는 중…"}
              />
              <div className="text-[10px] text-slate-400 tabular-nums flex items-center gap-3">
                <span>{promptDraft.length.toLocaleString()}자</span>
                {defaultPrompt && (
                  <span>기본값: {defaultPrompt.length.toLocaleString()}자</span>
                )}
                {defaultPrompt &&
                  promptDraft.trim() === defaultPrompt.trim() && (
                    <span className="text-slate-500">(기본값과 동일)</span>
                  )}
              </div>
            </div>
            <div className="px-4 py-3 border-t border-slate-200 flex items-center gap-2">
              <button
                className="px-3 py-1 text-[12px] border border-slate-300 rounded hover:bg-slate-50"
                onClick={() => defaultPrompt && setPromptDraft(defaultPrompt)}
                disabled={!defaultPrompt}
                title="에디터를 기본 프롬프트 내용으로 채우기 (저장은 아님)"
              >
                ↺ 기본값 불러오기
              </button>
              {customPrompt && (
                <button
                  className="px-3 py-1 text-[12px] border border-rose-300 text-rose-700 rounded hover:bg-rose-50"
                  onClick={resetPromptToDefault}
                  title="저장된 커스텀 프롬프트 삭제 → 기본값 사용으로 복귀"
                >
                  🗑 커스텀 삭제
                </button>
              )}
              <span className="ml-auto" />
              <button
                className="px-3 py-1 text-[12px] border border-slate-300 rounded hover:bg-slate-50"
                onClick={() => setPromptEditorOpen(false)}
              >
                취소
              </button>
              <button
                className="px-3 py-1 text-[12px] bg-indigo-600 text-white rounded hover:bg-indigo-700"
                onClick={savePromptDraft}
                title="저장 후 LLM 재검증 실행 시 이 프롬프트를 사용"
              >
                💾 저장
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI HS 분류 evidence modal — cell 클릭으로 진입 */}
      <InvoiceLineEvidence
        open={!!evidenceModal}
        onClose={() => setEvidenceModal(null)}
        rowIndex={evidenceModal?.rowIndex}
        description={evidenceModal?.description}
        composition={evidenceModal?.composition}
        onConfirm={handleEvidenceConfirm}
      />
    </div>
  );
};

export default TrainingVerifyPanel;
